import '@babylonjs/loaders';
import {
    Engine,
    Scene,
    Vector3,
    MeshBuilder,
    StandardMaterial,
    Color3,
    ArcRotateCamera,
    UniversalCamera,
    KeyboardEventTypes,
    HemisphericLight,
    Color4,
    Observer,
    Scalar,
    KeyboardInfo,
    Texture,
    DynamicTexture,
    Sound,
    ActionManager,
    ExecuteCodeAction,
    PointLight,
    TransformNode
} from '@babylonjs/core';
import { CSG } from '@babylonjs/core';
import { GridMaterial } from '@babylonjs/materials/grid';
import {
    AdvancedDynamicTexture,
    Rectangle,
    Line,
    Ellipse,
    TextBlock,
    Button,
    Control,
    InputText
} from '@babylonjs/gui';
import { io, Socket } from 'socket.io-client';

// Declare the showMobileControls function from the global window object
declare global {
    interface Window {
        showMobileControls?: (show: boolean) => void;
        initializeEngineSound?: () => void;
    }
}

// Import the image files directly so Parcel will include them in the build
import customSegment1 from './assets/custom_segment1.jpg';
import customSegment2 from './assets/custom_segment2.jpg';

// Use the imported image paths
const customImagePaths = [
    customSegment1,
    customSegment2
];

// Get the canvas element
const canvas = document.getElementById('renderCanvas');
if (!canvas || !(canvas instanceof HTMLCanvasElement)) {
    throw new Error("Couldn't find the canvas element");
}

// Initialize the Babylon Engine
const engine = new Engine(canvas, true);

// Multiplayer variables
let socket: Socket | null = null;
let username = '';
let otherPlayers: Record<string, any> = {};

// Make the socket available globally so it can be accessed from different parts of the code
let globalSocket: Socket | null = null;

// Interface for leaderboard entries 
interface LeaderboardData {
    username: string;
    kilometers: number;
}

// Helper function to sanitize text for XSS prevention
function sanitizeText(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

// Create a safe HTML rendering function
function createSafeTextElement(text: string, element: string = 'span'): HTMLElement {
    const el = document.createElement(element);
    el.textContent = text; // This automatically escapes HTML
    return el;
}

// Create a scene
const createScene = (): {
    scene: Scene,
    enableDriving: () => void,
    loadKilometersFromStorage: () => void,
    resetKilometers: () => void
} => {
    // Create a new scene
    const scene = new Scene(engine);

    // Set a synthwave background color - dark blue/purple gradient feel
    scene.clearColor = new Color4(0.05, 0.0, 0.15, 1.0); // Deep purple/blue

    // Add a light with synthwave coloring
    const light = new HemisphericLight("light", new Vector3(0, 1, 0), scene);
    light.intensity = 0.7;
    light.diffuse = new Color3(0.9, 0.2, 0.9); // Pinkish light
    light.groundColor = new Color3(0.1, 0.1, 0.5); // Deep purple for ground reflection

    // Create a camera that we'll move with WASD keys
    const camera = new UniversalCamera('camera', new Vector3(0, 1.5, -10), scene);

    // Adjust camera angle to look like sitting in a car driver's seat
    // Pitch the camera upward slightly - about 5-10 degrees
    camera.rotation.x = -Math.PI * 0.03; // Slight upward tilt

    // Don't auto-attach controls as we'll handle this ourselves
    camera.attachControl(canvas, false);

    // === CUSTOM ACCELERATION SYSTEM ===

    // Disable the built-in keyboard controls so we can implement our own
    camera.inputs.removeByType("FreeCameraKeyboardMoveInput");

    // Acceleration and speed settings
    const vehicleSettings = {
        maxSpeed: 2.0,         // Max speed (arbitrary units, will feel like ~200 km/h)
        acceleration: 0.001,     // How quickly we accelerate
        deceleration: 0.0003,     // How quickly we decelerate when not pressing W
        turnSpeed: 0.008,        // How fast we can turn - increased for sharper turning
        currentSpeed: 0,        // Current forward speed
        // Track which keys are pressed
        keysPressed: {
            w: false,
            a: false,
            s: false,
            d: false,
            space: false       // For turbo boost
        },
        inputEnabled: false,
        // Turbo boost settings
        turboBoost: {
            active: false,     // Is turbo boost currently active?
            amount: 1.0,       // Current amount (1.0 = full, 0.0 = empty)
            maxAmount: 1.0,    // Maximum amount
            depletionRate: 0.00333,  // 20% per second (0.2/60 since we multiply by deltaTime * 60)
            regenRate: 0.001,  // How quickly it regenerates per frame (10% every 10s)
            speedMultiplier: 1.5, // Max speed multiplier when boosting
            accelerationMultiplier: 3.0 // Acceleration multiplier when boosting
        }
    };

    // Keyboard event listeners
    scene.onKeyboardObservable.add((kbInfo) => {
        // Only process keyboard input if driving is enabled
        if (!vehicleSettings.inputEnabled) {
            return;
        }

        switch (kbInfo.type) {
            case KeyboardEventTypes.KEYDOWN:
                // Update key state
                switch (kbInfo.event.key.toLowerCase()) {
                    case 'w':
                        vehicleSettings.keysPressed.w = true;
                        break;
                    case 'a':
                        vehicleSettings.keysPressed.a = true;
                        break;
                    case 's':
                        vehicleSettings.keysPressed.s = true;
                        break;
                    case 'd':
                        vehicleSettings.keysPressed.d = true;
                        break;
                    case ' ':
                        vehicleSettings.keysPressed.space = true;
                        break;
                }
                break;
            case KeyboardEventTypes.KEYUP:
                // Update key state
                switch (kbInfo.event.key.toLowerCase()) {
                    case 'w':
                        vehicleSettings.keysPressed.w = false;
                        break;
                    case 'a':
                        vehicleSettings.keysPressed.a = false;
                        break;
                    case 's':
                        vehicleSettings.keysPressed.s = false;
                        break;
                    case 'd':
                        vehicleSettings.keysPressed.d = false;
                        break;
                    case ' ':
                        vehicleSettings.keysPressed.space = false;
                        break;
                }
                break;
        }
    });

    // Movement calculation in the render loop
    const movementObserver = scene.onBeforeRenderObservable.add(() => {
        // Calculate deltaTime for frame-rate independent movement
        const deltaTime = engine.getDeltaTime() / 1000;

        // Previous speed (for detecting acceleration/deceleration)
        const previousSpeed = vehicleSettings.currentSpeed;

        // Check and update turbo boost state - now handled in the speed display update
        vehicleSettings.turboBoost.active = vehicleSettings.keysPressed.space && vehicleSettings.turboBoost.amount > 0;

        // Handle acceleration/deceleration
        if (vehicleSettings.keysPressed.w) {
            // Apply acceleration with turbo boost if active
            let currentAcceleration = vehicleSettings.acceleration;
            if (vehicleSettings.turboBoost.active) {
                currentAcceleration *= vehicleSettings.turboBoost.accelerationMultiplier;
            }

            // Accelerate when W is pressed (gradually)
            vehicleSettings.currentSpeed += currentAcceleration;

            // Determine max speed with turbo boost
            let effectiveMaxSpeed = vehicleSettings.maxSpeed;
            if (vehicleSettings.turboBoost.active) {
                effectiveMaxSpeed *= vehicleSettings.turboBoost.speedMultiplier;
            }

            // Clamp to effective max speed
            vehicleSettings.currentSpeed = Math.min(vehicleSettings.currentSpeed, effectiveMaxSpeed);
        } else {
            // Decelerate when W is not pressed
            vehicleSettings.currentSpeed -= vehicleSettings.deceleration;
            // Clamp to minimum of 0
            vehicleSettings.currentSpeed = Math.max(vehicleSettings.currentSpeed, 0);
        }

        // Apply braking/reverse with S key
        if (vehicleSettings.keysPressed.s) {
            vehicleSettings.currentSpeed -= vehicleSettings.deceleration * 7;
            // Allow for slight reverse
            vehicleSettings.currentSpeed = Math.max(vehicleSettings.currentSpeed, -vehicleSettings.maxSpeed * 1);
        }

        // Update engine sound based on speed
        if (isSoundInitialized && !engineSoundMuted) {
            // Calculate a playback rate between 0.5 (idle) and 2.0 (max speed)
            // Map the current speed to that range
            const speedFactor = Math.abs(vehicleSettings.currentSpeed) / vehicleSettings.maxSpeed;
            const playbackRate = 0.5 + (speedFactor * 1.5); // 0.5 to 2.0 range

            // Apply the new playback rate
            engineSound.setPlaybackRate(playbackRate);

            // Adjust volume slightly based on acceleration
            if (vehicleSettings.currentSpeed > previousSpeed + 0.001) {
                // When accelerating, slightly increase volume for a moment
                engineSound.setVolume(Math.min(0.5, engineSound.getVolume() + 0.05));
            } else {
                // Gradually return to normal volume
                engineSound.setVolume(Math.max(0.35, engineSound.getVolume() - 0.01));
            }
        }

        // Calculate forward movement vector based on camera direction
        const forward = camera.getDirection(Vector3.Forward());
        forward.y = 0; // Keep movement on ground plane
        forward.normalize();

        // Calculate movement amount this frame
        const movementDistance = vehicleSettings.currentSpeed * deltaTime * 60; // Scale by 60 for more natural movement

        // Apply forward/backward movement
        camera.position.addInPlace(forward.scale(movementDistance));

        // Handle turning (A and D keys)
        let rotationAmount = 0;

        if (vehicleSettings.keysPressed.a) {
            rotationAmount = -vehicleSettings.turnSpeed * deltaTime * 60;
        }

        if (vehicleSettings.keysPressed.d) {
            rotationAmount = vehicleSettings.turnSpeed * deltaTime * 60;
        }

        // Scale rotation amount by current speed for better handling at higher speeds
        // Only rotate if we're moving
        if (Math.abs(vehicleSettings.currentSpeed) > 0.01) {
            // Calculate a new speed factor that gives sharper turning at low speeds
            // Using an inverse exponential curve for more dramatic effect at low speeds
            const normalizedSpeed = Math.abs(vehicleSettings.currentSpeed) / vehicleSettings.maxSpeed;

            // This formula gives very sharp turning at low speeds, gradually reducing at higher speeds
            // The 3.0 value is a sharpness factor - higher values = even sharper low-speed turning
            const speedFactor = Math.pow(1 - normalizedSpeed, 3.0);

            // Apply the rotation with our new speed factor
            camera.rotation.y += rotationAmount * (1.0 + speedFactor * 4.0);
        }

        // Keep the camera at fixed height and maintain our car-like angle
        camera.position.y = 1.5;
        camera.rotation.x = -Math.PI * 0.03; // Maintain slight upward tilt

        // Update road positions based on camera movement
        updateRoad(camera.position);
    });

    // ========== SYNTHWAVE ROAD SYSTEM ==========

    // Road configuration
    const roadConfig = {
        segmentLength: 20,     // Length of each road segment
        width: 10,             // Width of the road
        segmentsAhead: 15,     // Number of segments to render ahead
        segmentsBehind: 3,     // Number of segments to render behind
        totalSegments: 0,      // Will track how many segments we've created
        textureTiling: 2,      // How many times to repeat the texture per segment
        middleLineWidth: 0.4,  // Width of the neon middle line
        dashLength: 3,         // Length of each dash in the middle line
        dashGap: 2,            // Gap between dashes
        billboardFrequency: 20 // Add a billboard every X segments
    };

    // Grid configuration
    const gridConfig = {
        segmentLength: 200,    // Length of each grid segment (much larger than road segments)
        width: 600,            // Width of each grid segment (covers both sides of the road)
        segmentsAhead: 3,      // Number of grid segments to render ahead
        segmentsBehind: 1,     // Number of grid segments to render behind
        totalSegments: 0,      // Will track how many grid segments we've created
        gridRatio: 10          // Size of grid cells - larger number = bigger squares
    };

    // Store all road segments
    const roadSegments: { mesh: any, position: number }[] = [];

    // Store all grid segments
    const gridSegments: { mesh: any, position: number }[] = [];

    // Create a synthwave road texture
    const createRoadTexture = (): Texture => {
        // We'll create a custom texture using a canvas
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 512;
        const context = canvas.getContext('2d');

        if (context) {
            // Dark purple road background
            context.fillStyle = '#150025';
            context.fillRect(0, 0, canvas.width, canvas.height);

            // Add grid lines for that synthwave look
            context.strokeStyle = '#ff00ff'; // Neon pink
            context.lineWidth = 1;

            // Horizontal grid lines
            const gridSpacing = 20;
            for (let y = 0; y < canvas.height; y += gridSpacing) {
                context.beginPath();
                context.moveTo(0, y);
                context.lineTo(canvas.width, y);
                context.stroke();
            }
        }

        // Create texture from canvas
        const roadTexture = new Texture("data:," + canvas.toDataURL().split(",")[1], scene);
        return roadTexture;
    };

    // Create the road texture
    const roadTexture = createRoadTexture();

    // Function to create a lamp post for a road segment
    const createLampPost = (segment: any, position: number) => {
        // Create pole (vertical part)
        const pole = MeshBuilder.CreateCylinder(
            `lampPole_${roadConfig.totalSegments}`,
            {
                height: 7, // Tall pole
                diameter: 0.2, // Thin pole
                tessellation: 12, // Smooth cylinder
            },
            scene
        );

        // Create arm (horizontal part that extends over the road)
        const arm = MeshBuilder.CreateCylinder(
            `lampArm_${roadConfig.totalSegments}`,
            {
                height: 3, // Length of arm
                diameter: 0.15, // Thinner than pole
                tessellation: 12,
            },
            scene
        );

        // Create lamp head (light source)
        const lampHead = MeshBuilder.CreateSphere(
            `lampHead_${roadConfig.totalSegments}`,
            {
                diameter: 0.6,
                segments: 16, // Smooth sphere
            },
            scene
        );

        // Create light source emanating from the lamp
        const lampLight = new PointLight(
            `lampLight_${roadConfig.totalSegments}`,
            new Vector3(0, 0, 0), // Will be positioned later
            scene
        );
        lampLight.diffuse = new Color3(1.0, 0.6, 0.2); // Warm orange light
        lampLight.intensity = 0.7; // Slightly brighter
        lampLight.range = 15; // How far the light reaches

        // Create materials
        // Pole material (shiny grey metal)
        const poleMaterial = new StandardMaterial(`poleMat_${roadConfig.totalSegments}`, scene);
        poleMaterial.diffuseColor = new Color3(0.7, 0.7, 0.75); // Lighter grey
        poleMaterial.specularColor = new Color3(0.9, 0.9, 0.9); // Very reflective
        poleMaterial.specularPower = 32; // More spread reflection for shinier look

        // Light material (warm orange glow)
        const lightMaterial = new StandardMaterial(`lightMat_${roadConfig.totalSegments}`, scene);
        lightMaterial.diffuseColor = new Color3(1.0, 0.6, 0.2); // Warm orange
        lightMaterial.emissiveColor = new Color3(1.0, 0.6, 0.2); // Warm orange glow
        lightMaterial.specularColor = new Color3(1, 1, 1);

        // Apply materials
        pole.material = poleMaterial;
        arm.material = poleMaterial;
        lampHead.material = lightMaterial;

        // Position the elements
        // Pole at the side of the road
        pole.position = new Vector3(
            -roadConfig.width / 2 - 2, // Left side of road with small margin
            3.5, // Half the height (pivot is at center)
            position // Z position matches the segment
        );

        // Arm extends from top of pole toward the road
        arm.position = new Vector3(
            -roadConfig.width / 2 - 0.5, // Extends inward
            6.5, // Top of pole
            position // Same z as pole
        );
        arm.rotation = new Vector3(0, 0, Math.PI / 2); // Rotate to horizontal

        // Lamp head at the end of the arm
        lampHead.position = new Vector3(
            -roadConfig.width / 2 + 1, // Extends over road edge
            6.5, // Same height as arm
            position // Same z as pole
        );

        // Position the light at the lamp head
        lampLight.position = lampHead.position;

        // Make all lamp parts children of the segment for easier management
        pole.parent = segment;
        arm.parent = segment;
        lampHead.parent = segment;
    };

    // Function to create a road segment
    const createRoadSegment = (position: number): any => {
        // Create a segment ground mesh for the road
        const segment = MeshBuilder.CreateGround(
            `roadSegment_${roadConfig.totalSegments++}`,
            {
                width: roadConfig.width,
                height: roadConfig.segmentLength,
                subdivisions: 4  // Increase subdivisions for better texture mapping
            },
            scene
        );

        // Position the segment along the Z axis
        segment.position.z = position;
        segment.position.y = 0;
        segment.position.x = 0;

        // Create and apply road material with synthwave styling
        const roadMaterial = new StandardMaterial(`roadMat_${roadConfig.totalSegments}`, scene);
        roadMaterial.diffuseTexture = roadTexture;
        roadMaterial.emissiveColor = new Color3(0.1, 0, 0.2); // Slight purple glow
        roadMaterial.specularColor = new Color3(0.2, 0.2, 0.2); // Subtle shine

        // Set texture tiling
        if (roadMaterial.diffuseTexture) {
            try {
                // @ts-ignore - TypeScript definitions may not match runtime properties
                roadMaterial.diffuseTexture.vScale = roadConfig.textureTiling;
                // @ts-ignore
                roadMaterial.diffuseTexture.uScale = 1;
            } catch (e) {
                console.warn("Could not set texture scaling", e);
            }
        }

        // Apply the material to the segment
        segment.material = roadMaterial;

        // Create dashed middle lines instead of a continuous line
        const dashCycle = roadConfig.dashLength + roadConfig.dashGap; // Total length of one dash + gap
        const numberOfDashes = Math.floor(roadConfig.segmentLength / dashCycle);

        // Create a neon material for the middle line
        const middleLineMat = new StandardMaterial(`middleLineMat_${roadConfig.totalSegments}`, scene);
        middleLineMat.diffuseColor = new Color3(0, 1, 1); // Cyan
        middleLineMat.emissiveColor = new Color3(0, 1, 1); // Make it glow
        middleLineMat.specularColor = new Color3(1, 1, 1); // Full white specular for shine

        // Create individual dashes along the segment
        for (let i = 0; i < numberOfDashes; i++) {
            // Calculate position for this dash
            const dashPosition = (i * dashCycle) - (roadConfig.segmentLength / 2) + (roadConfig.dashLength / 2);

            // Create a ground mesh for this dash
            const dash = MeshBuilder.CreateGround(
                `middleDash_${roadConfig.totalSegments}_${i}`,
                {
                    width: roadConfig.middleLineWidth,
                    height: roadConfig.dashLength,
                    subdivisions: 1
                },
                scene
            );

            // Parent to the road segment
            dash.parent = segment;

            // Position the dash along the segment
            dash.position = new Vector3(0, 0.01, dashPosition);

            // Apply the neon material
            dash.material = middleLineMat;
        }

        // Create edge lines (neon pink)
        const leftEdge = MeshBuilder.CreateGround(
            `leftEdge_${roadConfig.totalSegments}`,
            {
                width: 0.2,
                height: roadConfig.segmentLength,
                subdivisions: 1
            },
            scene
        );
        const rightEdge = MeshBuilder.CreateGround(
            `rightEdge_${roadConfig.totalSegments}`,
            {
                width: 0.2,
                height: roadConfig.segmentLength,
                subdivisions: 1
            },
            scene
        );

        // Create material for the edge lines (neon pink)
        const edgeLineMat = new StandardMaterial(`edgeLineMat_${roadConfig.totalSegments}`, scene);
        edgeLineMat.diffuseColor = new Color3(1, 0, 0.7); // Pink
        edgeLineMat.emissiveColor = new Color3(1, 0, 0.7); // Make it glow
        edgeLineMat.specularColor = new Color3(1, 1, 1); // Shiny

        // Apply the material to the edges
        leftEdge.material = edgeLineMat;
        rightEdge.material = edgeLineMat;

        // Parent and position the edge lines
        leftEdge.parent = segment;
        rightEdge.parent = segment;
        leftEdge.position = new Vector3(-roadConfig.width / 2 + 0.1, 0.01, 0);
        rightEdge.position = new Vector3(roadConfig.width / 2 - 0.1, 0.01, 0);

        // Add a billboard every X segments
        if (roadConfig.totalSegments % roadConfig.billboardFrequency === 0) {
            // Create a billboard on the right side
            const billboard = MeshBuilder.CreatePlane(
                `billboard_${roadConfig.totalSegments}`,
                {
                    width: 9,
                    height: 4.5
                },
                scene
            );

            // Position the billboard to the right of the road
            billboard.parent = segment;
            billboard.position = new Vector3(roadConfig.width / 2 + 10, 2.5, 0);
            billboard.rotation = new Vector3(0, Math.PI / 4, 0); // 45 degrees instead of 90

            // Create custom billboard content
            createCustomBillboardTexture(billboard, roadConfig.totalSegments);
        }

        // Add a lamp post to every segment
        createLampPost(segment, 0); // Center of segment

        // Add to our segments array
        roadSegments.push({
            mesh: segment,
            position: position
        });

        return segment;
    };

    // Function to update road position based on camera movement
    const updateRoad = (cameraPosition: Vector3) => {
        // Get camera Z position
        const cameraZ = cameraPosition.z;

        // Calculate segment positions
        const furthestSegmentNeeded = Math.floor(cameraZ / roadConfig.segmentLength) + roadConfig.segmentsAhead;
        const nearestSegmentNeeded = Math.floor(cameraZ / roadConfig.segmentLength) - roadConfig.segmentsBehind;

        // Calculate the next segment that should have a billboard based on camera position
        const currentSegmentIndex = Math.floor(cameraZ / roadConfig.segmentLength);
        const nextBillboardSegmentIndex = Math.ceil(currentSegmentIndex / roadConfig.billboardFrequency) * roadConfig.billboardFrequency;
        const billboardSegmentPosition = nextBillboardSegmentIndex * roadConfig.segmentLength;

        // Ensure we create segments up to and including the billboard position that's 15 segments ahead
        const billboardSegmentNeeded = nextBillboardSegmentIndex + roadConfig.billboardFrequency;

        // Remove segments too far behind
        for (let i = roadSegments.length - 1; i >= 0; i--) {
            const segmentPos = roadSegments[i].position;

            // If segment is too far behind the camera, remove it
            if (segmentPos < nearestSegmentNeeded * roadConfig.segmentLength) {
                roadSegments[i].mesh.dispose();
                roadSegments.splice(i, 1);
            }
        }

        // Add new segments ahead if needed, ensuring we include the segment with the billboard
        for (let i = nearestSegmentNeeded; i <= Math.max(furthestSegmentNeeded, billboardSegmentNeeded); i++) {
            const segmentPos = i * roadConfig.segmentLength;

            // Check if segment already exists at this position
            const existingSegment = roadSegments.find(seg =>
                Math.abs(seg.position - segmentPos) < 0.1
            );

            // If no segment exists, create one
            if (!existingSegment) {
                createRoadSegment(segmentPos);
            }
        }
    };

    // Create initial road segments
    for (let i = -roadConfig.segmentsBehind; i <= roadConfig.segmentsAhead; i++) {
        createRoadSegment(i * roadConfig.segmentLength);
    }

    // Create a billboard at a visible distance in front of the starting camera
    const createBillboardAtSegment = (segmentIndex: number) => {
        // Find the segment at the specified index
        const segment = roadSegments.find(seg =>
            Math.abs(seg.position - (segmentIndex * roadConfig.segmentLength)) < 0.1
        );

        // If the segment exists, add a billboard to it
        if (segment) {
            // Create a rectangular billboard
            const billboard = MeshBuilder.CreatePlane(
                `billboard_initial`,
                {
                    width: 9,
                    height: 4.5,
                },
                scene
            );

            // Position the billboard on the right side of the road, slightly elevated
            billboard.parent = segment.mesh;
            // Position it on the right side, 10 units away from the road edge
            billboard.position = new Vector3(roadConfig.width / 2 + 10, 2.5, 0);
            // Rotate it 45 degrees along the Y-axis (vertical/top-down axis)
            billboard.rotation.y = Math.PI / 4; // 45 degrees instead of 90

            // Make billboard clickable - open Stripe URL in new tab
            billboard.actionManager = new ActionManager(scene);
            billboard.actionManager.registerAction(
                new ExecuteCodeAction(
                    ActionManager.OnPickTrigger,
                    () => {
                        window.open('https://buy.stripe.com/bIYeVI8bf7fc1TacMM', '_blank');
                    }
                )
            );

            // Add hover cursor effect - change cursor to pointer when hovering over billboard
            billboard.actionManager.registerAction(
                new ExecuteCodeAction(
                    ActionManager.OnPointerOverTrigger,
                    () => {
                        document.body.style.cursor = 'pointer';
                    }
                )
            );

            // Reset cursor when no longer hovering
            billboard.actionManager.registerAction(
                new ExecuteCodeAction(
                    ActionManager.OnPointerOutTrigger,
                    () => {
                        document.body.style.cursor = 'default';
                    }
                )
            );

            // Create a dynamic texture for the billboard text
            const textureWidth = 512;
            const textureHeight = 256;
            const dynamicTexture = new DynamicTexture(`billboardTexture_initial`,
                { width: textureWidth, height: textureHeight }, scene, false);

            // Get the context for drawing
            const textureContext = dynamicTexture.getContext();

            // Fill the background
            textureContext.fillStyle = "#000000"; // Black background instead of dark gray
            textureContext.fillRect(0, 0, textureWidth, textureHeight);

            // Draw the text - need to cast to CanvasRenderingContext2D to access text properties
            const ctx = textureContext as unknown as CanvasRenderingContext2D;
            ctx.font = "bold 64px Arial";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillStyle = "#FFFFFF"; // Pure white text
            ctx.fillText("Click to buy Ad", textureWidth / 2, textureHeight / 2 - 30);

            // Add a second line of text in a different color
            ctx.font = "bold 40px Arial";
            ctx.fillStyle = "#FFFFFF"; // Also white text instead of cyan
            ctx.fillText("$10,000/month", textureWidth / 2, textureHeight / 2 + 40);

            // Update the texture
            dynamicTexture.update();

            // Create material with the texture
            const billboardMaterial = new StandardMaterial(`billboardMat_initial`, scene);
            billboardMaterial.diffuseTexture = dynamicTexture;
            billboardMaterial.specularColor = new Color3(0.2, 0.2, 0.2); // Subtle shine
            billboardMaterial.backFaceCulling = false; // Show both sides
            billboardMaterial.emissiveColor = new Color3(1, 1, 1); // Make the texture glow at full brightness
            billboardMaterial.ambientColor = new Color3(1, 1, 1); // Full ambient lighting

            // Apply material to the billboard
            billboard.material = billboardMaterial;

            // Add a neon border around the billboard
            // Create frame as individual edges to better control appearance
            const frameThickness = 0.15;
            const frameDepth = 0.05;

            // Create neon frame material
            const frameMaterial = new StandardMaterial(`frameMatBillboard_initial`, scene);
            frameMaterial.diffuseColor = new Color3(0, 1, 1); // Cyan
            frameMaterial.emissiveColor = new Color3(0, 1, 1); // Make it glow
            frameMaterial.specularColor = new Color3(1, 1, 1); // Shiny

            // Top edge
            const topFrame = MeshBuilder.CreateBox(
                `topFrame_initial`,
                { width: 9 + frameThickness, height: frameThickness, depth: frameDepth },
                scene
            );
            topFrame.parent = billboard;
            topFrame.position.y = 2.25 + frameThickness / 2; // Half of new height (4.5/2)
            topFrame.position.z = -frameDepth / 2;
            topFrame.material = frameMaterial;

            // Bottom edge
            const bottomFrame = MeshBuilder.CreateBox(
                `bottomFrame_initial`,
                { width: 9 + frameThickness, height: frameThickness, depth: frameDepth },
                scene
            );
            bottomFrame.parent = billboard;
            bottomFrame.position.y = -2.25 - frameThickness / 2; // Half of new height (4.5/2)
            bottomFrame.position.z = -frameDepth / 2;
            bottomFrame.material = frameMaterial;

            // Left edge
            const leftFrame = MeshBuilder.CreateBox(
                `leftFrame_initial`,
                { width: frameThickness, height: 4.5 + frameThickness, depth: frameDepth },
                scene
            );
            leftFrame.parent = billboard;
            leftFrame.position.x = -4.5 - frameThickness / 2; // Half of new width (9/2)
            leftFrame.position.z = -frameDepth / 2;
            leftFrame.material = frameMaterial;

            // Right edge
            const rightFrame = MeshBuilder.CreateBox(
                `rightFrame_initial`,
                { width: frameThickness, height: 4.5 + frameThickness, depth: frameDepth },
                scene
            );
            rightFrame.parent = billboard;
            rightFrame.position.x = 4.5 + frameThickness / 2; // Half of new width (9/2)
            rightFrame.position.z = -frameDepth / 2;
            rightFrame.material = frameMaterial;
        }
    };

    // Add a billboard to segment 1, which will be immediately visible at the start position
    createBillboardAtSegment(1);

    // Create the retro environment (mountains, sun, etc.)
    const createRetroEnvironment = () => {
        // Create the "floor" on both sides of the road with grid lines
        const leftSide = MeshBuilder.CreateGround(
            "leftSide",
            {
                width: 100,
                height: 1000
            },
            scene
        );

        const rightSide = MeshBuilder.CreateGround(
            "rightSide",
            {
                width: 100,
                height: 1000
            },
            scene
        );

        // Position them on either side of the road
        leftSide.position.x = -roadConfig.width - 50;
        rightSide.position.x = roadConfig.width + 50;

        // Grid configuration for infinite grid system - separate for left and right sides
        const gridSystemConfig = {
            segmentLength: 200,    // Length of each grid segment
            sideWidth: 300,        // Width of each side grid segment (not crossing the road)
            segmentsAhead: 3,      // Number of grid segments to render ahead
            segmentsBehind: 1,     // Number of grid segments to render behind
            totalSegmentsLeft: 0,  // Track segments for left side
            totalSegmentsRight: 0  // Track segments for right side
        };

        // Store all grid segments (left and right)
        const leftGridSegments: { mesh: any, position: number }[] = [];
        const rightGridSegments: { mesh: any, position: number }[] = [];

        // Create built-in GridMaterial for all grid segments
        const createGridMaterial = () => {
            const gridMaterial = new GridMaterial("gridMaterial", scene);

            // Configure grid material for classic 80s look with larger squares
            gridMaterial.gridRatio = 20; // Much larger cells for more spaced out grid
            gridMaterial.mainColor = new Color3(0.051, 0, 0.149); // #0D0026 - Dark purple to match road
            gridMaterial.lineColor = new Color3(1, 0, 0.7); // Bright magenta lines
            gridMaterial.opacity = 1.0; // Fully opaque
            gridMaterial.majorUnitFrequency = 5; // Major line every 5 cells
            gridMaterial.minorUnitVisibility = 1.0; // Make minor lines fully visible

            // Ensure the grid is visible from both sides
            gridMaterial.backFaceCulling = false;

            return gridMaterial;
        };

        // Create a shared grid material
        const gridMaterial = createGridMaterial();

        // Function to create a left grid segment
        const createLeftGridSegment = (position: number) => {
            // Create a grid segment for the left side
            const gridSegment = MeshBuilder.CreateGround(
                `leftGridSegment_${gridSystemConfig.totalSegmentsLeft++}`,
                {
                    width: gridSystemConfig.sideWidth,
                    height: gridSystemConfig.segmentLength
                },
                scene
            );

            // Position the segment along the Z axis and to the left of the road
            gridSegment.position.z = position;
            gridSegment.position.y = 0.005; // Slightly above ground to avoid z-fighting
            gridSegment.position.x = -roadConfig.width - (gridSystemConfig.sideWidth / 2); // Left of the road

            // Apply the shared grid material
            gridSegment.material = gridMaterial;

            // Add to grid segments array
            leftGridSegments.push({
                mesh: gridSegment,
                position: position
            });

            return gridSegment;
        };

        // Function to create a right grid segment
        const createRightGridSegment = (position: number) => {
            // Create a grid segment for the right side
            const gridSegment = MeshBuilder.CreateGround(
                `rightGridSegment_${gridSystemConfig.totalSegmentsRight++}`,
                {
                    width: gridSystemConfig.sideWidth,
                    height: gridSystemConfig.segmentLength
                },
                scene
            );

            // Position the segment along the Z axis and to the right of the road
            gridSegment.position.z = position;
            gridSegment.position.y = 0.005; // Slightly above ground to avoid z-fighting
            gridSegment.position.x = roadConfig.width + (gridSystemConfig.sideWidth / 2); // Right of the road

            // Apply the shared grid material
            gridSegment.material = gridMaterial;

            // Add to grid segments array
            rightGridSegments.push({
                mesh: gridSegment,
                position: position
            });

            return gridSegment;
        };

        // Create initial grid segments on both sides
        for (let i = -gridSystemConfig.segmentsBehind; i <= gridSystemConfig.segmentsAhead; i++) {
            const position = i * gridSystemConfig.segmentLength;
            createLeftGridSegment(position);
            createRightGridSegment(position);
        }

        // Function to update grid position based on camera movement
        const updateGridSystem = (cameraPosition: Vector3) => {
            // Get camera Z position
            const cameraZ = cameraPosition.z;

            // Calculate grid segment positions
            const furthestSegmentNeeded = Math.floor(cameraZ / gridSystemConfig.segmentLength) + gridSystemConfig.segmentsAhead;
            const nearestSegmentNeeded = Math.floor(cameraZ / gridSystemConfig.segmentLength) - gridSystemConfig.segmentsBehind;

            // Update left side grid segments

            // Remove segments too far behind
            for (let i = leftGridSegments.length - 1; i >= 0; i--) {
                const segmentPos = leftGridSegments[i].position;

                // If segment is too far behind the camera, remove it
                if (segmentPos < nearestSegmentNeeded * gridSystemConfig.segmentLength) {
                    leftGridSegments[i].mesh.dispose();
                    leftGridSegments.splice(i, 1);
                }
            }

            // Add new segments ahead if needed
            for (let i = nearestSegmentNeeded; i <= furthestSegmentNeeded; i++) {
                const segmentPos = i * gridSystemConfig.segmentLength;

                // Check if segment already exists at this position
                const existingSegment = leftGridSegments.find(seg =>
                    Math.abs(seg.position - segmentPos) < 0.1
                );

                // If no segment exists, create one
                if (!existingSegment) {
                    createLeftGridSegment(segmentPos);
                }
            }

            // Update right side grid segments

            // Remove segments too far behind
            for (let i = rightGridSegments.length - 1; i >= 0; i--) {
                const segmentPos = rightGridSegments[i].position;

                // If segment is too far behind the camera, remove it
                if (segmentPos < nearestSegmentNeeded * gridSystemConfig.segmentLength) {
                    rightGridSegments[i].mesh.dispose();
                    rightGridSegments.splice(i, 1);
                }
            }

            // Add new segments ahead if needed
            for (let i = nearestSegmentNeeded; i <= furthestSegmentNeeded; i++) {
                const segmentPos = i * gridSystemConfig.segmentLength;

                // Check if segment already exists at this position
                const existingSegment = rightGridSegments.find(seg =>
                    Math.abs(seg.position - segmentPos) < 0.1
                );

                // If no segment exists, create one
                if (!existingSegment) {
                    createRightGridSegment(segmentPos);
                }
            }
        };

        // Add the update function to the scene's onBeforeRenderObservable
        scene.onBeforeRenderObservable.add(() => {
            updateGridSystem(camera.position);
        });

        // Apply the grid material to side grounds too for consistent look
        leftSide.material = gridMaterial;
        rightSide.material = gridMaterial;

        // Create a synthwave sunset on the horizon
        // Use a much simpler approach for reliability
        const sunsetPlane = MeshBuilder.CreatePlane("sunset", { width: 800, height: 400 }, scene);

        // Position it where it will definitely be visible
        // Place it much closer so it's clearly in view
        sunsetPlane.position = new Vector3(-60, 0, 2000); // Lowered from 40 to 20 to align with horizon

        // Lock rotation to ensure it always faces the camera
        sunsetPlane.billboardMode = TransformNode.BILLBOARDMODE_ALL;

        // Create material for the sunset
        const sunsetMaterial = new StandardMaterial("sunsetMaterial", scene);
        sunsetMaterial.disableLighting = true; // Ensure full brightness
        sunsetMaterial.emissiveColor = new Color3(1, 1, 1); // Make it glow at full intensity

        // Create a dynamic texture for our sunset
        const sunsetTexture = new DynamicTexture("sunsetTexture", { width: 1024, height: 512 }, scene);
        const sunsetContext = sunsetTexture.getContext();

        // Fill background - dark purple/blue
        sunsetContext.fillStyle = "#0D0026";
        sunsetContext.fillRect(0, 0, 1024, 512);

        // Draw the sun (half-circle at horizon)
        const sunY = 256; // Middle of canvas height
        const sunRadius = 50;

        // Draw large glowing halo first (background glow)
        const haloGradient = sunsetContext.createRadialGradient(
            512, sunY, sunRadius * 0.7,
            512, sunY, sunRadius * 2
        );
        haloGradient.addColorStop(0, "rgba(255, 100, 50, 0.8)");
        haloGradient.addColorStop(1, "rgba(255, 50, 100, 0)");

        sunsetContext.fillStyle = haloGradient;
        sunsetContext.beginPath();
        sunsetContext.arc(512, sunY, sunRadius * 2, Math.PI, 0, false);
        sunsetContext.fill();

        // Draw sun with gradient
        const sunGradient = sunsetContext.createLinearGradient(0, sunY - sunRadius, 0, sunY);
        sunGradient.addColorStop(0, "#ffcc00"); // Yellow at top
        sunGradient.addColorStop(0.3, "#ff8800"); // Orange
        sunGradient.addColorStop(0.6, "#ff5500"); // Orange-red
        sunGradient.addColorStop(1, "#ff0066"); // Pink at bottom

        sunsetContext.fillStyle = sunGradient;
        sunsetContext.beginPath();
        sunsetContext.arc(512, sunY, sunRadius, Math.PI, 0, false);
        sunsetContext.fill();

        // Draw horizontal lines across the sun (the iconic 80s look)
        sunsetContext.strokeStyle = "#000000";
        sunsetContext.lineWidth = 2;

        // Draw 5 evenly spaced lines
        for (let i = 1; i <= 5; i++) {
            const y = sunY - sunRadius + ((sunRadius * i) / 6);
            sunsetContext.beginPath();
            sunsetContext.moveTo(512 - sunRadius, y);
            sunsetContext.lineTo(512 + sunRadius, y);
            sunsetContext.stroke();
        }

        // Update the texture
        sunsetTexture.update();

        // Apply the texture to the material
        sunsetMaterial.diffuseTexture = sunsetTexture;
        sunsetMaterial.specularColor = new Color3(0, 0, 0); // No specular highlights

        // Apply material to the plane
        sunsetPlane.material = sunsetMaterial;

        // Keep the sunset fixed relative to the camera's z position
        scene.registerBeforeRender(() => {
            // Always position the sunset ahead of the camera
            sunsetPlane.position.z = camera.position.z + 300;
        });
    };

    // Create the retro environment
    createRetroEnvironment();

    // Add synthwave-styled speedometer (initially hidden)
    const speedDisplay = document.createElement('div');
    speedDisplay.style.position = 'absolute';
    speedDisplay.style.bottom = '20px';
    speedDisplay.style.left = '20px';
    speedDisplay.style.color = '#00ffff'; // Cyan text
    speedDisplay.style.backgroundColor = 'rgba(21, 0, 37, 0.7)'; // Semi-transparent purple
    speedDisplay.style.padding = window.innerWidth < 600 ? '5px 10px' : '10px 20px'; // Smaller padding on mobile
    speedDisplay.style.fontFamily = '"Press Start 2P", monospace'; // Retro font (fallback to monospace)
    speedDisplay.style.fontSize = window.innerWidth < 600 ? '12px' : '16px'; // Smaller font on mobile
    speedDisplay.style.borderRadius = '5px';
    speedDisplay.style.border = '2px solid #ff00aa'; // Pink border
    speedDisplay.style.boxShadow = '0 0 10px #ff00aa, 0 0 5px #ff00aa'; // Pink glow
    speedDisplay.style.display = 'none'; // Initially hidden
    document.body.appendChild(speedDisplay);

    // Create the kilometer counter UI
    const kmCounter = document.createElement('div');
    kmCounter.style.position = 'absolute';
    kmCounter.style.top = '20px';
    kmCounter.style.right = '20px';
    kmCounter.style.color = '#ff00aa'; // Pink text
    kmCounter.style.backgroundColor = 'rgba(21, 0, 37, 0.7)'; // Semi-transparent purple
    kmCounter.style.padding = window.innerWidth < 600 ? '5px 10px' : '10px 20px'; // Smaller padding on mobile
    kmCounter.style.fontFamily = '"Press Start 2P", monospace'; // Retro font
    kmCounter.style.fontSize = window.innerWidth < 600 ? '12px' : '16px'; // Smaller font on mobile
    kmCounter.style.borderRadius = '5px';
    kmCounter.style.border = '2px solid #00ffff'; // Cyan border
    kmCounter.style.boxShadow = '0 0 10px #00ffff, 0 0 5px #00ffff'; // Cyan glow
    kmCounter.style.display = 'none'; // Initially hidden
    document.body.appendChild(kmCounter);

    // Create the player count display UI
    const playerCountDisplay = document.createElement('div');
    playerCountDisplay.style.position = 'absolute';
    playerCountDisplay.style.top = '60px'; // Below the kilometer counter
    playerCountDisplay.style.right = '20px';
    playerCountDisplay.style.color = '#00ffff'; // Cyan text
    playerCountDisplay.style.backgroundColor = 'rgba(21, 0, 37, 0.7)'; // Semi-transparent purple
    playerCountDisplay.style.padding = window.innerWidth < 600 ? '5px 10px' : '10px 20px';
    playerCountDisplay.style.fontFamily = '"Press Start 2P", monospace';
    playerCountDisplay.style.fontSize = window.innerWidth < 600 ? '10px' : '14px'; // Slightly smaller than km counter
    playerCountDisplay.style.borderRadius = '5px';
    playerCountDisplay.style.border = '2px solid #ff00aa'; // Pink border
    playerCountDisplay.style.boxShadow = '0 0 10px #ff00aa, 0 0 5px #ff00aa';
    playerCountDisplay.style.display = 'none'; // Initially hidden
    playerCountDisplay.textContent = 'Players Online: 0';
    document.body.appendChild(playerCountDisplay);

    // Add responsive sizing handler for window resize
    window.addEventListener('resize', () => {
        // Update speedometer styling for responsive sizing
        speedDisplay.style.padding = window.innerWidth < 600 ? '5px 10px' : '10px 20px';
        speedDisplay.style.fontSize = window.innerWidth < 600 ? '12px' : '16px';

        // Update km counter styling for responsive sizing
        kmCounter.style.padding = window.innerWidth < 600 ? '5px 10px' : '10px 20px';
        kmCounter.style.fontSize = window.innerWidth < 600 ? '12px' : '16px';

        // Update player count display styling for responsive sizing
        playerCountDisplay.style.padding = window.innerWidth < 600 ? '5px 10px' : '10px 20px';
        playerCountDisplay.style.fontSize = window.innerWidth < 600 ? '10px' : '14px';
    });

    // Create the turbo boost UI
    const turboBoostContainer = document.createElement('div');
    turboBoostContainer.style.position = 'absolute';
    turboBoostContainer.style.bottom = '70px'; // Position above speed display (which is at bottom: 20px)
    turboBoostContainer.style.left = '20px'; // Align with speed display on the left
    turboBoostContainer.style.width = '200px';
    turboBoostContainer.style.height = '25px';
    turboBoostContainer.style.backgroundColor = 'rgba(255, 255, 255, 0.7)'; // White background
    turboBoostContainer.style.borderRadius = '5px';
    turboBoostContainer.style.border = '2px solid #00ffff'; // Cyan border
    turboBoostContainer.style.boxShadow = '0 0 10px #00ffff, 0 0 5px #00ffff'; // Cyan glow
    turboBoostContainer.style.overflow = 'hidden'; // For the inner bar
    turboBoostContainer.style.display = 'none'; // Initially hidden
    document.body.appendChild(turboBoostContainer);

    // Create the inner turbo boost bar
    const turboBoostBar = document.createElement('div');
    turboBoostBar.style.position = 'absolute';
    turboBoostBar.style.top = '0';
    turboBoostBar.style.left = '0';
    turboBoostBar.style.width = '100%'; // Full initially
    turboBoostBar.style.height = '100%';
    turboBoostBar.style.backgroundColor = '#0088ff'; // Blue fill
    turboBoostBar.style.transition = 'width 0.2s'; // Smooth transition
    turboBoostContainer.appendChild(turboBoostBar);

    // Add turbo boost label
    const turboBoostLabel = document.createElement('div');
    turboBoostLabel.style.position = 'absolute';
    turboBoostLabel.style.top = '50%';
    turboBoostLabel.style.left = '50%';
    turboBoostLabel.style.transform = 'translate(-50%, -50%)';
    turboBoostLabel.style.color = '#000000'; // Black text
    turboBoostLabel.style.fontFamily = '"Press Start 2P", monospace';
    turboBoostLabel.style.fontSize = '9px'; // Smaller to fit more text
    turboBoostLabel.style.textShadow = '0 0 3px rgba(255, 255, 255, 0.7)';
    turboBoostLabel.style.whiteSpace = 'nowrap'; // Keep all text on one line
    turboBoostLabel.textContent = 'TURBO [SPACE BAR]';
    turboBoostContainer.appendChild(turboBoostLabel);

    // Initialize the total distance
    let totalKilometers = 0;

    // Function to reset the kilometer counter (no longer loads from localStorage)
    const loadKilometersFromStorage = () => {
        // No longer loading from localStorage - always start at zero
        totalKilometers = 0;
        // Set initial display
        kmCounter.textContent = '000000.0 KM';
        console.log('Starting new driving session at 0.00 kilometers');
    };

    // No longer saving kilometers to localStorage
    const saveKilometersToStorage = (km: number) => {
        // Function kept for compatibility but no longer does anything
        // This prevents having to update all call sites
    };

    // Reset kilometers to zero
    const resetKilometers = () => {
        totalKilometers = 0;
        kmCounter.textContent = '000000.0 KM';
    };

    // Try to load saved kilometers on scene creation
    loadKilometersFromStorage();

    // Update the speed display and kilometer counter
    scene.onBeforeRenderObservable.add(() => {
        // Calculate speed in km/h (approximate)
        let speedKmh = 0;

        if (vehicleSettings.turboBoost.active) {
            // Boosted speed calculation - up to 450 km/h when boosting at max speed
            speedKmh = Math.abs(vehicleSettings.currentSpeed) * (450 / (vehicleSettings.maxSpeed * vehicleSettings.turboBoost.speedMultiplier));
        } else {
            // Regular speed calculation - up to 288 km/h at max speed (unchanged)
            speedKmh = Math.abs(vehicleSettings.currentSpeed) * (288 / vehicleSettings.maxSpeed);
        }

        speedDisplay.textContent = `SPEED: ${Math.floor(speedKmh)} KM/H`;

        // Calculate distance traveled this frame
        const deltaTime = engine.getDeltaTime() / 1000; // Time in seconds
        const distanceThisFrame = (speedKmh / 3600) * deltaTime; // Convert km/h to km/s and multiply by time

        // Only add distance when moving forward
        if (vehicleSettings.currentSpeed > 0) {
            totalKilometers += distanceThisFrame;
            // No longer saving to localStorage
        }

        // Update turbo boost 
        // Deplete turbo boost if active
        if (vehicleSettings.turboBoost.active) {
            vehicleSettings.turboBoost.amount -= vehicleSettings.turboBoost.depletionRate * deltaTime * 60;
            vehicleSettings.turboBoost.amount = Math.max(0, vehicleSettings.turboBoost.amount);

            // Deactivate if empty
            if (vehicleSettings.turboBoost.amount <= 0) {
                vehicleSettings.turboBoost.active = false;
            }
        }
        // Regenerate turbo boost when not active and not full
        else if (vehicleSettings.turboBoost.amount < vehicleSettings.turboBoost.maxAmount) {
            vehicleSettings.turboBoost.amount += vehicleSettings.turboBoost.regenRate * deltaTime * 60;
            vehicleSettings.turboBoost.amount = Math.min(vehicleSettings.turboBoost.maxAmount, vehicleSettings.turboBoost.amount);
        }

        // Update turbo boost UI
        const boostPercentage = (vehicleSettings.turboBoost.amount / vehicleSettings.turboBoost.maxAmount) * 100;
        turboBoostBar.style.width = `${boostPercentage}%`;

        // Update active state visual indication
        if (vehicleSettings.turboBoost.active) {
            turboBoostBar.style.backgroundColor = '#00ffff'; // Cyan when active
            turboBoostContainer.style.borderColor = '#00ffff';
            turboBoostContainer.style.boxShadow = '0 0 15px #00ffff, 0 0 10px #00ffff'; // Stronger glow
        } else {
            turboBoostBar.style.backgroundColor = '#0088ff'; // Blue when inactive
            turboBoostContainer.style.borderColor = '#00ffff';
            turboBoostContainer.style.boxShadow = '0 0 10px #00ffff, 0 0 5px #00ffff'; // Normal glow
        }

        // Format kilometers with one decimal place for tenths
        const wholeKm = Math.floor(totalKilometers);
        const tenthsKm = Math.floor((totalKilometers - wholeKm) * 10); // Get the tenths digit
        const formattedKm = wholeKm.toString().padStart(6, '0');
        kmCounter.textContent = `${formattedKm}.${tenthsKm} KM`;
    });

    // Engine sound implementation
    // Use a reliable local sound file path - we'll need to add this file to the project
    const engineSoundUrl = "/sounds/engine-47745.mp3"; // Local file path
    let engineSound: Sound;
    let isSoundInitialized = false;

    // Initialize engine sound - we'll call this after user interaction
    const initializeEngineSound = () => {
        // If already initialized, don't do it again
        if (isSoundInitialized) return;

        console.log("Initializing engine sound...");

        try {
            // First, make sure the sound isn't already playing
            if (engineSound) {
                engineSound.dispose();
            }

            // Create the engine sound with error handling
            engineSound = new Sound(
                "lamborghiniEngine",
                engineSoundUrl,
                scene,
                // Success callback
                () => {
                    console.log("🔊 Engine sound loaded successfully");
                    isSoundInitialized = true;
                    engineSound.loop = true;
                    engineSound.setVolume(0.25); // Reduced volume by 50% (was 0.5)

                    // Start the sound at idle pitch
                    engineSound.play();
                    console.log("🏎️ Engine sound started playing");

                    // Set initial low pitch for idle
                    engineSound.setPlaybackRate(0.5);

                },
                // Options
                {
                    spatialSound: false // We want this sound to follow the camera
                }
            );

            // Add error handler
            engineSound.onended = () => {
                console.log("Engine sound ended unexpectedly");
                isSoundInitialized = false;
            };

        } catch (error: any) {
            console.error("❌ Exception initializing engine sound:", error);
            // Try to play the fallback HTML audio element
            const fallbackSound = document.getElementById('engine-sound') as HTMLAudioElement;
            if (fallbackSound) {
                fallbackSound.play().catch(e => {
                    console.error("Failed to play fallback sound:", e);
                });
            }
        }
    };

    // Toggle engine sound mute/unmute
    let engineSoundMuted = false;
    const toggleEngineSound = () => {
        if (!isSoundInitialized || !engineSound) {
            console.log("Engine sound not initialized yet, can't toggle");
            return false;
        }

        // Toggle mute status
        engineSoundMuted = !engineSoundMuted;

        if (engineSoundMuted) {
            // Mute the sound - both set volume to 0 and pause it
            engineSound.setVolume(0);
            engineSound.pause(); // Completely stop the sound
            console.log("Engine sound muted");
        } else {
            // Unmute the sound - restore volume and resume playing
            engineSound.setVolume(0.25); // Reduced volume by 50% (was 0.5)

            // If the sound was completely stopped, restart it
            if (!engineSound.isPlaying) {
                engineSound.play();
            }

            // Set the playback rate based on current speed
            const speedFactor = Math.abs(vehicleSettings.currentSpeed) / vehicleSettings.maxSpeed;
            const playbackRate = 0.5 + (speedFactor * 1.5); // 0.5 to 2.0 range
            engineSound.setPlaybackRate(playbackRate);

            console.log("Engine sound unmuted");
        }

        return engineSoundMuted; // Return current muted state
    };

    // Make functions available globally
    window.initializeEngineSound = initializeEngineSound;
    window.toggleEngineSound = toggleEngineSound;

    // Function to create a player cube for multiplayer visualization
    const createPlayerCube = (playerId: string, playerData: any) => {
        // Check if this player already has a cube
        if (otherPlayers[playerId] && otherPlayers[playerId].mesh) {
            return otherPlayers[playerId].mesh;
        }

        // Create a cube to represent the player
        const playerMesh = MeshBuilder.CreateBox(
            `player_${playerId}`,
            { width: 3, height: 2, depth: 5 },
            scene
        );

        // Create material for the player with a neon synthwave color
        const playerMaterial = new StandardMaterial(`playerMat_${playerId}`, scene);

        // Expanded synthwave neon color palette with 10 vibrant colors
        const synthwaveColors = [
            new Color3(1.0, 0.0, 1.0),   // Hot Pink
            new Color3(0.0, 1.0, 1.0),   // Cyan
            new Color3(0.6, 0.0, 0.8),   // Purple
            new Color3(1.0, 0.4, 0.0),   // Orange
            new Color3(0.0, 0.8, 1.0),   // Electric Blue
            new Color3(1.0, 0.8, 0.0),   // Neon Gold
            new Color3(0.2, 1.0, 0.5),   // Neon Green
            new Color3(0.9, 0.0, 0.5),   // Magenta
            new Color3(0.0, 1.0, 0.4),   // Toxic Green
            new Color3(1.0, 0.2, 0.6)    // Flamingo Pink
        ];

        // Randomly select a color from our palette (true random for each new player)
        const colorIndex = Math.floor(Math.random() * synthwaveColors.length);
        const selectedColor = synthwaveColors[colorIndex];

        // Make the material highly emissive for that neon glow effect
        playerMaterial.diffuseColor = selectedColor;
        playerMaterial.emissiveColor = selectedColor.scale(1.5); // Even stronger glow effect
        playerMaterial.specularPower = 50; // More shiny

        // Add metallic reflection for that chrome effect
        playerMaterial.specularColor = new Color3(1, 1, 1);

        playerMesh.material = playerMaterial;

        // Add visible white edges to the cube
        playerMesh.enableEdgesRendering();
        playerMesh.edgesWidth = 6.0; // Make the edges extra thick
        playerMesh.edgesColor = new Color4(1, 1, 1, 1); // Bright white edges

        // Position the player at their reported position
        playerMesh.position = new Vector3(
            playerData.position.x,
            playerData.position.y,
            playerData.position.z
        );

        // Add player name as a label above the cube
        const playerNamePlane = MeshBuilder.CreatePlane(
            `playerName_${playerId}`,
            { width: 4, height: 1 },
            scene
        );
        playerNamePlane.parent = playerMesh;
        playerNamePlane.position = new Vector3(0, 2, 0); // Position above the cube

        // Create a dynamic texture for the player name
        const nameTexture = new DynamicTexture(
            `nameTexture_${playerId}`,
            { width: 256, height: 64 },
            scene
        );
        const nameContext = nameTexture.getContext();
        nameContext.fillStyle = 'black';
        nameContext.fillRect(0, 0, 256, 64);

        // Draw the player name
        const ctx = nameContext as unknown as CanvasRenderingContext2D;
        ctx.font = 'bold 40px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = 'white';

        // Sanitize username before displaying
        const sanitizedUsername = sanitizeText(playerData.username);
        ctx.fillText(sanitizedUsername, 128, 32);
        nameTexture.update();

        // Create material for the name plate
        const nameMaterial = new StandardMaterial(`nameMat_${playerId}`, scene);
        nameMaterial.diffuseTexture = nameTexture;
        nameMaterial.emissiveTexture = nameTexture; // Make it glow
        nameMaterial.backFaceCulling = false; // Show from both sides
        playerNamePlane.material = nameMaterial;

        // Billboard mode to always face the camera
        playerNamePlane.billboardMode = 7; // Billboard on all axes

        // Store the player's mesh for later updates
        otherPlayers[playerId] = {
            mesh: playerMesh,
            namePlate: playerNamePlane,
            username: playerData.username
        };

        return playerMesh;
    };

    // Function to update a player's position
    const updatePlayerPosition = (playerId: string, position: any, rotation: any) => {
        if (otherPlayers[playerId] && otherPlayers[playerId].mesh) {
            // Update the player's position and rotation
            otherPlayers[playerId].mesh.position = new Vector3(
                position.x,
                position.y,
                position.z
            );

            // Update rotation (just y-axis rotation for this simple implementation)
            if (rotation && rotation.y !== undefined) {
                otherPlayers[playerId].mesh.rotation = new Vector3(0, rotation.y, 0);
            }
        }
    };

    // Function to remove a player
    const removePlayer = (playerId: string) => {
        if (otherPlayers[playerId]) {
            if (otherPlayers[playerId].mesh) {
                otherPlayers[playerId].mesh.dispose();
            }
            if (otherPlayers[playerId].namePlate) {
                otherPlayers[playerId].namePlate.dispose();
            }
            delete otherPlayers[playerId];
        }
    };

    // Setup Socket.IO connection and event handlers
    const setupMultiplayer = () => {
        // Create Socket.IO connection using environment variable
        const serverUrl = import.meta.env.VITE_RENDER_SERVER_URL || 'http://localhost:3000';
        console.log('Connecting to multiplayer server at:', serverUrl);

        // Initialize the socket connection
        socket = io(serverUrl);

        // Make socket available globally
        globalSocket = socket;

        // Connection established
        socket.on('connect', () => {
            console.log('Connected to multiplayer server:', socket?.id);

            // Join the game with our user data
            socket!.emit('join', {
                username: username,
                kilometers: totalKilometers,
                position: {
                    x: camera.position.x,
                    y: camera.position.y,
                    z: camera.position.z
                },
                rotation: {
                    y: camera.rotation.y
                }
            });

            // Request leaderboard data when connecting
            socket!.emit('requestLeaderboard');
        });

        // Listen for leaderboard data from server
        socket.on('leaderboardData', (leaderboardData: LeaderboardData[]) => {
            console.log('Received leaderboard data from server:', leaderboardData);
            if ((window as any).updateLeaderboardUI) {
                (window as any).updateLeaderboardUI(leaderboardData);
            } else {
                console.warn('UpdateLeaderboardUI function is not yet available');
            }
        });

        // Helper function to update player count display
        function updatePlayerCount(players: { [id: string]: any }) {
            const count = Object.keys(players).length;
            if (playerCountDisplay) {
                playerCountDisplay.textContent = `Players Online: ${count}`;
                // Show the player count display if it's not already visible
                if (playerCountDisplay.style.display === 'none') {
                    playerCountDisplay.style.display = 'block';
                }
            }

            // Request current player count from server
            if (socket !== null && socket !== undefined) {
                socket.emit('requestPlayerCount');
            }
        }

        // Receive existing players
        socket.on('currentPlayers', (players) => {
            console.log('Received current players:', players);

            // Clear any existing players
            for (const id in otherPlayers) {
                removePlayer(id);
            }

            // Create cubes for all players except our own
            for (const id in players) {
                if (id !== socket?.id) {
                    createPlayerCube(id, players[id]);
                }
            }

            // Update player count display
            updatePlayerCount(players);
        });

        // Handle new player joining
        socket.on('newPlayer', (playerData) => {
            console.log('New player joined:', playerData);

            // Create a cube for the new player
            createPlayerCube(playerData.id, playerData);

            // Request current players to update player count
            if (socket) {
                socket.emit('requestPlayerCount');
            }
        });

        // Handle player movement
        socket.on('playerMoved', (data) => {
            updatePlayerPosition(data.id, data.position, data.rotation);
        });

        // Handle player disconnection
        socket.on('playerDisconnected', (playerId) => {
            console.log('Player disconnected:', playerId);
            removePlayer(playerId);

            // Request current players to update player count
            if (socket !== null && socket !== undefined) {
                socket.emit('requestPlayerCount');
            }
        });

        // Listen for player count updates from server
        socket.on('playerCount', (count) => {
            console.log(`Player count updated: ${count} players online`);
            if (playerCountDisplay) {
                playerCountDisplay.textContent = `Players Online: ${count}`;
                // Show the player count display if it's not already visible
                if (playerCountDisplay.style.display === 'none') {
                    playerCountDisplay.style.display = 'block';
                }
            }
        });

        // Set up position updates
        scene.onBeforeRenderObservable.add(() => {
            if (socket && socket.connected && vehicleSettings.inputEnabled) {
                // Only send updates when the position has changed significantly
                // This helps reduce network traffic
                const posChanged = true; // For simplicity, always send updates

                if (posChanged) {
                    socket.emit('updatePosition', {
                        position: {
                            x: camera.position.x,
                            y: camera.position.y,
                            z: camera.position.z
                        },
                        rotation: {
                            y: camera.rotation.y
                        },
                        kilometers: totalKilometers
                    });
                }
            }
        });
    };

    // Return scene with methods
    return {
        scene,
        enableDriving: () => {
            console.log("enableDriving function called!");
            vehicleSettings.inputEnabled = true;
            console.log("Vehicle controls enabled! inputEnabled =", vehicleSettings.inputEnabled);

            // Reset and show turbo boost
            vehicleSettings.turboBoost.amount = vehicleSettings.turboBoost.maxAmount;
            vehicleSettings.turboBoost.active = false;
            turboBoostContainer.style.display = 'block';
            turboBoostBar.style.width = '100%';

            // Initialize and start the engine sound - this happens after user interaction
            // which allows browsers to play audio
            initializeEngineSound();

            // Initialize multiplayer if username is provided
            if (username) {
                setupMultiplayer();
            }

            // Ensure keyboard events are processed by adding a new direct handler
            window.addEventListener('keydown', (event) => {
                if (!vehicleSettings.inputEnabled) return;

                const key = event.key.toLowerCase();
                switch (key) {
                    case 'w':
                        vehicleSettings.keysPressed.w = true;
                        break;
                    case 'a':
                        vehicleSettings.keysPressed.a = true;
                        break;
                    case 's':
                        vehicleSettings.keysPressed.s = true;
                        break;
                    case 'd':
                        vehicleSettings.keysPressed.d = true;
                        break;
                    case ' ':
                        vehicleSettings.keysPressed.space = true;
                        break;
                }
            });

            window.addEventListener('keyup', (event) => {
                if (!vehicleSettings.inputEnabled) return;

                const key = event.key.toLowerCase();
                switch (key) {
                    case 'w':
                        vehicleSettings.keysPressed.w = false;
                        break;
                    case 'a':
                        vehicleSettings.keysPressed.a = false;
                        break;
                    case 's':
                        vehicleSettings.keysPressed.s = false;
                        break;
                    case 'd':
                        vehicleSettings.keysPressed.d = false;
                        break;
                    case ' ':
                        vehicleSettings.keysPressed.space = false;
                        break;
                }
            });

            // Key display functionality removed as requested
        },
        loadKilometersFromStorage,
        resetKilometers
    };
};

// Create our scene
const sceneWithControls = createScene();
const scene = sceneWithControls.scene;

// Create a variable to track if driving is enabled
let drivingEnabled = false;

// Create a start screen overlay before starting the game
const createStartScreen = () => {
    console.log("Creating start screen...");

    // Create fullscreen UI
    const uiTexture = AdvancedDynamicTexture.CreateFullscreenUI("startScreen");

    // Main container for all elements
    const container = new Rectangle();
    container.width = 1;
    container.height = 1;
    container.background = "rgba(0, 0, 36, 0.85)"; // Dark blue semi-transparent background
    container.thickness = 0;
    uiTexture.addControl(container);

    // Define interface for statistics data
    interface StatisticsData {
        totalPlayers: number;
        totalMinutesPlayed: number;
        totalKilometersDriven: number;
    }

    // Variable to store statistics data
    let statisticsData: StatisticsData = {
        totalPlayers: 0,
        totalMinutesPlayed: 0,
        totalKilometersDriven: 0
    };

    // Create the HTML statistics button (above leaderboard button)
    const statisticsButton = document.createElement('button');
    statisticsButton.textContent = "STATISTICS";
    statisticsButton.style.position = 'absolute';
    statisticsButton.style.bottom = window.innerWidth < 600 ? '120px' : '80px'; // Higher position on small screens
    statisticsButton.style.left = '20px';
    statisticsButton.style.padding = window.innerWidth < 600 ? '5px 8px' : '8px 16px';
    statisticsButton.style.width = window.innerWidth < 600 ? '120px' : '160px';
    statisticsButton.style.height = window.innerWidth < 600 ? '30px' : '40px';
    statisticsButton.style.backgroundColor = 'white';
    statisticsButton.style.color = 'black';
    statisticsButton.style.border = 'none';
    statisticsButton.style.borderRadius = '5px';
    statisticsButton.style.fontFamily = '"Press Start 2P", monospace';
    statisticsButton.style.fontSize = window.innerWidth < 600 ? '7px' : '10px';
    statisticsButton.style.cursor = 'pointer';
    statisticsButton.style.boxShadow = 'none';
    statisticsButton.style.zIndex = '1000';
    statisticsButton.style.display = 'block';
    statisticsButton.style.textAlign = 'center';
    statisticsButton.style.overflow = 'hidden';
    statisticsButton.style.whiteSpace = 'nowrap';
    document.body.appendChild(statisticsButton);

    // Create the HTML leaderboard button
    const leaderboardButton = document.createElement('button');
    leaderboardButton.textContent = "LEADERBOARD";
    leaderboardButton.style.position = 'absolute';
    leaderboardButton.style.bottom = window.innerWidth < 600 ? '80px' : '30px'; // Higher position on small screens
    leaderboardButton.style.left = '20px';
    leaderboardButton.style.padding = window.innerWidth < 600 ? '5px 8px' : '8px 16px'; // Reduced padding
    leaderboardButton.style.width = window.innerWidth < 600 ? '120px' : '160px'; // Increased width
    leaderboardButton.style.height = window.innerWidth < 600 ? '30px' : '40px';
    leaderboardButton.style.backgroundColor = 'white'; // Changed from '#ff00aa' to white
    leaderboardButton.style.color = 'black'; // Changed from 'white' to black
    leaderboardButton.style.border = 'none'; // Removed the cyan border
    leaderboardButton.style.borderRadius = '5px';
    leaderboardButton.style.fontFamily = '"Press Start 2P", monospace';
    leaderboardButton.style.fontSize = window.innerWidth < 600 ? '7px' : '10px'; // Reduced font size
    leaderboardButton.style.cursor = 'pointer';
    leaderboardButton.style.boxShadow = 'none'; // Removed the cyan glow
    leaderboardButton.style.zIndex = '1000';
    leaderboardButton.style.display = 'block'; // Ensure it's visible initially
    leaderboardButton.style.textAlign = 'center'; // Ensure text is centered
    leaderboardButton.style.overflow = 'hidden'; // Prevent text overflow
    leaderboardButton.style.whiteSpace = 'nowrap'; // Prevent text wrapping
    document.body.appendChild(leaderboardButton);

    // Create statistics panel that will be toggled
    const statisticsPanel = new Rectangle("statisticsPanel");
    statisticsPanel.width = window.innerWidth < 600 ? "300px" : "400px";
    statisticsPanel.height = window.innerWidth < 600 ? "250px" : "300px"; // Slightly smaller than leaderboard
    statisticsPanel.background = "rgba(21, 0, 37, 0.9)";
    statisticsPanel.cornerRadius = 10;
    statisticsPanel.thickness = 2;
    statisticsPanel.color = "#00ffff";
    statisticsPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    statisticsPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    statisticsPanel.left = "0px";
    statisticsPanel.top = "0px";
    statisticsPanel.isVisible = false; // Hidden by default
    statisticsPanel.zIndex = 2000;
    statisticsPanel.isPointerBlocker = true; // Block pointer events
    container.addControl(statisticsPanel);

    // Create leaderboard panel that will be toggled
    const leaderboardPanel = new Rectangle("leaderboardPanel");
    leaderboardPanel.width = window.innerWidth < 600 ? "300px" : "400px";
    leaderboardPanel.height = window.innerWidth < 600 ? "350px" : "450px"; // Increased height to fit all entries
    leaderboardPanel.background = "rgba(21, 0, 37, 0.9)";
    leaderboardPanel.cornerRadius = 10;
    leaderboardPanel.thickness = 2;
    leaderboardPanel.color = "#00ffff";
    leaderboardPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER; // Center horizontally
    leaderboardPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER; // Center vertically
    leaderboardPanel.left = "0px"; // Center position
    leaderboardPanel.top = "0px"; // Center position
    leaderboardPanel.isVisible = false; // Hidden by default
    leaderboardPanel.zIndex = 2000; // Increase z-index to be higher than start button (1000)
    container.addControl(leaderboardPanel);

    // Create a modal overlay to block interaction with other elements
    const modalOverlay = new Rectangle("modalOverlay");
    modalOverlay.width = 1;
    modalOverlay.height = 1;
    modalOverlay.background = "rgba(0, 0, 0, 0.7)"; // Semi-transparent black
    modalOverlay.thickness = 0;
    modalOverlay.isVisible = false; // Hidden by default
    modalOverlay.zIndex = 1999; // Just below the leaderboard panel but above start button
    modalOverlay.isPointerBlocker = true; // Block pointer events to elements behind
    container.addControl(modalOverlay);

    // Make leaderboard panel also block pointer events
    leaderboardPanel.isPointerBlocker = true;

    // Add click handler to close the leaderboard when clicking outside
    modalOverlay.onPointerDownObservable.add(() => {
        // Close both panels when clicking on the overlay
        leaderboardPanel.isVisible = false;
        statisticsPanel.isVisible = false;
        modalOverlay.isVisible = false;

        // Hide HTML close buttons
        statisticsCloseBtn.style.display = 'none';
        leaderboardCloseBtn.style.display = 'none';

        // Show the username input when closing the panels by clicking outside
        htmlUsernameInput.style.display = 'block';
    });

    // Add header to the statistics panel
    const statisticsHeader = new TextBlock();
    statisticsHeader.text = "STATISTICS";
    statisticsHeader.color = "#ff00aa";
    statisticsHeader.fontFamily = "'Press Start 2P', monospace";
    statisticsHeader.fontSize = window.innerWidth < 600 ? "11px" : "16px";
    statisticsHeader.height = "25px";
    statisticsHeader.top = window.innerWidth < 600 ? "-100px" : "-120px";
    statisticsPanel.addControl(statisticsHeader);

    // Create an HTML close button for statistics panel
    const statisticsCloseBtn = document.createElement('button');
    statisticsCloseBtn.textContent = "×"; // Using multiplication sign for a better X
    statisticsCloseBtn.style.position = 'fixed';
    statisticsCloseBtn.style.width = '30px';
    statisticsCloseBtn.style.height = '30px';
    statisticsCloseBtn.style.backgroundColor = '#ff0066';
    statisticsCloseBtn.style.color = 'white';
    statisticsCloseBtn.style.border = 'none';
    statisticsCloseBtn.style.borderRadius = '50%';
    statisticsCloseBtn.style.fontFamily = 'Arial, sans-serif';
    statisticsCloseBtn.style.fontSize = '20px';
    statisticsCloseBtn.style.fontWeight = 'bold';
    statisticsCloseBtn.style.cursor = 'pointer';
    statisticsCloseBtn.style.display = 'none';
    statisticsCloseBtn.style.zIndex = '2001';
    statisticsCloseBtn.style.lineHeight = '26px';
    statisticsCloseBtn.style.textAlign = 'center';
    statisticsCloseBtn.style.padding = '0';
    document.body.appendChild(statisticsCloseBtn);

    // Position function for statistics close button (called whenever the modal is shown)
    const positionStatisticsCloseBtn = () => {
        // Calculate modal dimensions
        const modalWidth = window.innerWidth < 600 ? 300 : 400;

        // Position at top-right of modal with a small margin
        const centerX = window.innerWidth / 2;
        const centerY = window.innerHeight / 2;
        const btnX = centerX + (modalWidth / 2) - 35; // 5px margin from right edge
        const btnY = centerY - (window.innerWidth < 600 ? 125 : 150) + 5; // 5px margin from top edge

        statisticsCloseBtn.style.left = btnX + 'px';
        statisticsCloseBtn.style.top = btnY + 'px';
    };

    // Event listener for statistics close button
    statisticsCloseBtn.addEventListener('click', () => {
        console.log('Statistics HTML close button clicked');
        statisticsPanel.isVisible = false;
        modalOverlay.isVisible = false;
        statisticsCloseBtn.style.display = 'none';

        // Show the username input again
        htmlUsernameInput.style.display = 'block';
    });

    // Create statistics text elements
    const createStatisticsText = (text: string, top: string) => {
        const textBlock = new TextBlock();
        textBlock.text = text;
        textBlock.color = "white";
        textBlock.fontFamily = "'Press Start 2P', monospace";
        textBlock.fontSize = window.innerWidth < 600 ? "10px" : "12px";
        textBlock.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        textBlock.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        textBlock.top = top;
        textBlock.left = "0px";
        textBlock.width = "90%";
        return textBlock;
    };

    // Create statistics text elements for small and large screens
    let totalPlayersLabelText: TextBlock, totalPlayersValueText: TextBlock;
    let totalMinutesLabelText: TextBlock, totalMinutesValueText: TextBlock;
    let totalKilometersLabelText: TextBlock, totalKilometersValueText: TextBlock;

    // For all screen sizes - create 6 text blocks (labels and values separately)
    totalPlayersLabelText = createStatisticsText("Total players", "-80px");
    totalPlayersValueText = createStatisticsText("connected: 0", "-55px");

    totalMinutesLabelText = createStatisticsText("Total minutes", "-20px");
    totalMinutesValueText = createStatisticsText("played: 0", "5px");

    totalKilometersLabelText = createStatisticsText("Total kilometers", "40px");
    totalKilometersValueText = createStatisticsText("driven: 0.0", "65px");

    // Add all text elements to panel
    statisticsPanel.addControl(totalPlayersLabelText);
    statisticsPanel.addControl(totalPlayersValueText);
    statisticsPanel.addControl(totalMinutesLabelText);
    statisticsPanel.addControl(totalMinutesValueText);
    statisticsPanel.addControl(totalKilometersLabelText);
    statisticsPanel.addControl(totalKilometersValueText);

    // Function to update statistics UI
    const updateStatisticsUI = (data: StatisticsData) => {
        // Format numbers with commas for thousands
        const formatNumber = (num: number) => {
            return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        };

        // Format kilometer value with one decimal point
        const formatKilometers = (km: number) => {
            const formattedValue = km.toFixed(1);
            return formatNumber(parseFloat(formattedValue));
        };

        // Update for all screen sizes - split across two lines for each statistic
        totalPlayersLabelText.text = "Total players";
        totalPlayersValueText.text = `connected: ${formatNumber(data.totalPlayers)}`;

        totalMinutesLabelText.text = "Total minutes";
        totalMinutesValueText.text = `played: ${formatNumber(data.totalMinutesPlayed)}`;

        totalKilometersLabelText.text = "Total kilometers";
        totalKilometersValueText.text = `driven: ${formatKilometers(data.totalKilometersDriven)}`;
    };

    // Toggle statistics visibility on button click
    statisticsButton.addEventListener('click', () => {
        if (!statisticsPanel.isVisible) {
            console.log('Opening statistics panel and requesting fresh data');

            // Hide the username input when opening the statistics
            htmlUsernameInput.style.display = 'none';

            // Close leaderboard panel if it's open
            if (leaderboardPanel.isVisible) {
                leaderboardPanel.isVisible = false;
                leaderboardCloseBtn.style.display = 'none';
                console.log('Closing leaderboard panel before opening statistics panel');
            }

            // Set initial "Loading..." text
            // For all screen sizes
            totalPlayersLabelText.text = "Total players";
            totalPlayersValueText.text = "connected: Loading...";

            totalMinutesLabelText.text = "Total minutes";
            totalMinutesValueText.text = "played: Loading...";

            totalKilometersLabelText.text = "Total kilometers";
            totalKilometersValueText.text = "driven: Loading...";

            // Show the panel and overlay
            statisticsPanel.isVisible = true;
            modalOverlay.isVisible = true;
            statisticsCloseBtn.style.display = 'block';
            positionStatisticsCloseBtn(); // Position the close button

            // Use tempSocket for real-time data if we don't have a globalSocket yet
            if (!globalSocket) {
                console.log('No global socket yet, creating a temporary one');
                const socketServerUrl = import.meta.env.VITE_RENDER_SERVER_URL || 'http://localhost:3000';
                const tempSocket = io(socketServerUrl);

                tempSocket.on('connect', () => {
                    console.log('Temp socket connected to request statistics');
                    tempSocket.emit('requestStatistics');

                    tempSocket.on('statisticsData', (data) => {
                        console.log('Received statistics data on temp socket:', data);

                        // Map server property names to frontend property names
                        const mappedData: StatisticsData = {
                            totalPlayers: data.totalPlayersConnected,
                            totalMinutesPlayed: data.totalMinutesPlayed,
                            totalKilometersDriven: data.totalKilometersDriven
                        };

                        statisticsData = mappedData;
                        updateStatisticsUI(mappedData);

                        // Disconnect the temporary socket after receiving data
                        tempSocket.disconnect();
                    });
                });
            } else {
                // Use the global socket if available
                console.log('Using global socket to request statistics');
                globalSocket.emit('requestStatistics');
            }
        } else {
            // Hide the panel and overlay when clicking the button again
            statisticsPanel.isVisible = false;
            modalOverlay.isVisible = false;
            statisticsCloseBtn.style.display = 'none';

            // Show the username input again
            htmlUsernameInput.style.display = 'block';
        }
    });

    // Add handler for statistics data from server
    if (globalSocket) {
        globalSocket.on('statisticsData', (data) => {
            console.log('Received statistics data:', data);

            // Map server property names to frontend property names
            const mappedData: StatisticsData = {
                totalPlayers: data.totalPlayersConnected,
                totalMinutesPlayed: data.totalMinutesPlayed,
                totalKilometersDriven: data.totalKilometersDriven
            };

            statisticsData = mappedData;
            updateStatisticsUI(mappedData);
        });
    }

    // Add header to the leaderboard
    const leaderboardHeader = new TextBlock();
    leaderboardHeader.text = "ALL-TIME LEADERBOARD";
    leaderboardHeader.color = "#ff00aa";
    leaderboardHeader.fontFamily = "'Press Start 2P', monospace";
    leaderboardHeader.fontSize = window.innerWidth < 600 ? "11px" : "16px";
    leaderboardHeader.height = "25px"; // Further reduced height
    leaderboardHeader.top = window.innerWidth < 600 ? "-150px" : "-170px"; // Move slightly above the top edge to reduce space
    leaderboardPanel.addControl(leaderboardHeader);

    // Create an HTML close button for leaderboard panel
    const leaderboardCloseBtn = document.createElement('button');
    leaderboardCloseBtn.textContent = "×"; // Using multiplication sign for a better X
    leaderboardCloseBtn.style.position = 'fixed';
    leaderboardCloseBtn.style.width = '30px';
    leaderboardCloseBtn.style.height = '30px';
    leaderboardCloseBtn.style.backgroundColor = '#ff0066';
    leaderboardCloseBtn.style.color = 'white';
    leaderboardCloseBtn.style.border = 'none';
    leaderboardCloseBtn.style.borderRadius = '50%';
    leaderboardCloseBtn.style.fontFamily = 'Arial, sans-serif';
    leaderboardCloseBtn.style.fontSize = '20px';
    leaderboardCloseBtn.style.fontWeight = 'bold';
    leaderboardCloseBtn.style.cursor = 'pointer';
    leaderboardCloseBtn.style.display = 'none';
    leaderboardCloseBtn.style.zIndex = '2001';
    leaderboardCloseBtn.style.lineHeight = '26px';
    leaderboardCloseBtn.style.textAlign = 'center';
    leaderboardCloseBtn.style.padding = '0';
    document.body.appendChild(leaderboardCloseBtn);

    // Position function for leaderboard close button (called whenever the modal is shown)
    const positionLeaderboardCloseBtn = () => {
        // Calculate modal dimensions
        const modalWidth = window.innerWidth < 600 ? 300 : 400;

        // Position at top-right of modal with a small margin
        const centerX = window.innerWidth / 2;
        const centerY = window.innerHeight / 2;
        const btnX = centerX + (modalWidth / 2) - 35; // 5px margin from right edge
        const btnY = centerY - (window.innerWidth < 600 ? 175 : 225) + 5; // 5px margin from top edge

        leaderboardCloseBtn.style.left = btnX + 'px';
        leaderboardCloseBtn.style.top = btnY + 'px';
    };

    // Event listener for leaderboard close button
    leaderboardCloseBtn.addEventListener('click', () => {
        console.log('Leaderboard HTML close button clicked');
        leaderboardPanel.isVisible = false;
        modalOverlay.isVisible = false;
        leaderboardCloseBtn.style.display = 'none';

        // Show the username input again
        htmlUsernameInput.style.display = 'block';
    });

    // Toggle leaderboard visibility on HTML button click
    leaderboardButton.addEventListener('click', () => {
        // Always fetch fresh data when opening
        if (!leaderboardPanel.isVisible) {
            console.log('Opening leaderboard and requesting fresh data');

            // Hide the username input when opening the leaderboard
            htmlUsernameInput.style.display = 'none';

            // Close statistics panel if it's open
            if (statisticsPanel.isVisible) {
                statisticsPanel.isVisible = false;
                statisticsCloseBtn.style.display = 'none';
                console.log('Closing statistics panel before opening leaderboard panel');
            }

            // Clear any existing entries and show loading message
            for (let i = 0; i < leaderboardEntries.length; i++) {
                leaderboardEntries[i].name.text = "Loading...";
                leaderboardEntries[i].km.text = "...";
            }

            // Make the panel and overlay visible immediately to show loading state
            leaderboardPanel.isVisible = true;
            modalOverlay.isVisible = true; // Show the overlay to block interaction
            leaderboardCloseBtn.style.display = 'block';
            positionLeaderboardCloseBtn(); // Position the close button

            // Now request the data
            if (globalSocket && globalSocket.connected) {
                console.log('Socket connected, requesting leaderboard data');
                globalSocket.emit('requestLeaderboard');
            } else {
                console.log('Socket not connected, attempting to connect');

                // Try to establish connection
                const serverUrl = import.meta.env.VITE_RENDER_SERVER_URL || 'http://localhost:3000';
                console.log('Connecting to server at:', serverUrl);
                const tempSocket = io(serverUrl);

                // Set up ALL event listeners before making the socket global
                globalSocket = setupSocketEventListeners(tempSocket);

                tempSocket.on('connect', () => {
                    console.log('Connected to server, requesting leaderboard data');
                    tempSocket.emit('requestLeaderboard');
                });
            }
        } else {
            // If already open, just close it
            leaderboardPanel.isVisible = false;
            modalOverlay.isVisible = false; // Hide the overlay as well
            leaderboardCloseBtn.style.display = 'none';

            // Show the username input again when closing the leaderboard
            htmlUsernameInput.style.display = 'block';
        }
    });

    // Add table headers
    const rankHeader = new TextBlock();
    rankHeader.text = "RANK";
    rankHeader.color = "white";
    rankHeader.fontFamily = "'Press Start 2P', monospace";
    rankHeader.fontSize = window.innerWidth < 600 ? "10px" : "12px";
    rankHeader.height = "20px";
    rankHeader.top = "-120px"; // Moved up further
    rankHeader.left = "-120px";
    rankHeader.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    leaderboardPanel.addControl(rankHeader);

    const nameHeader = new TextBlock();
    nameHeader.text = "USERNAME";
    nameHeader.color = "white";
    nameHeader.fontFamily = "'Press Start 2P', monospace";
    nameHeader.fontSize = window.innerWidth < 600 ? "10px" : "12px";
    nameHeader.height = "20px";
    nameHeader.top = "-120px"; // Moved up further
    nameHeader.left = "0px";
    nameHeader.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    leaderboardPanel.addControl(nameHeader);

    const kmHeader = new TextBlock();
    kmHeader.text = "KM";
    kmHeader.color = "white";
    kmHeader.fontFamily = "'Press Start 2P', monospace";
    kmHeader.fontSize = window.innerWidth < 600 ? "10px" : "12px";
    kmHeader.height = "20px";
    kmHeader.top = "-120px"; // Moved up further
    kmHeader.left = "120px";
    kmHeader.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    leaderboardPanel.addControl(kmHeader);

    // Create placeholders for leaderboard entries
    interface LeaderboardEntry {
        row: Rectangle;
        rank: TextBlock;
        name: TextBlock;
        km: TextBlock;
    }

    const leaderboardEntries: LeaderboardEntry[] = [];
    for (let i = 0; i < 10; i++) {
        const entryRow = new Rectangle();
        entryRow.width = "90%";
        entryRow.height = "25px"; // Keep current height
        entryRow.thickness = 0;
        entryRow.top = -90 + i * 28; // Start rows much closer to the column headers
        entryRow.background = i % 2 === 0 ? "rgba(0, 0, 0, 0.3)" : "rgba(0, 255, 255, 0.1)";
        leaderboardPanel.addControl(entryRow);

        const rankText = new TextBlock();
        rankText.name = `rank_${i}`;
        rankText.text = `#${i + 1}`;
        rankText.color = i < 3 ? "#ffff00" : "white"; // Gold for top 3
        rankText.fontFamily = "'Press Start 2P', monospace";
        rankText.fontSize = window.innerWidth < 600 ? "10px" : "12px";
        rankText.left = "-120px";
        rankText.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        entryRow.addControl(rankText);

        const nameText = new TextBlock();
        nameText.name = `name_${i}`;
        nameText.text = "---";
        nameText.color = "white";
        nameText.fontFamily = "'Press Start 2P', monospace";
        nameText.fontSize = window.innerWidth < 600 ? "10px" : "12px";
        nameText.left = "0px";
        nameText.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        entryRow.addControl(nameText);

        const kmText = new TextBlock();
        kmText.name = `km_${i}`;
        kmText.text = "0.0";
        kmText.color = "#00ffff";
        kmText.fontFamily = "'Press Start 2P', monospace";
        kmText.fontSize = window.innerWidth < 600 ? "10px" : "12px";
        kmText.left = window.innerWidth < 600 ? "100px" : "120px";
        kmText.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        entryRow.addControl(kmText);

        leaderboardEntries.push({ row: entryRow, rank: rankText, name: nameText, km: kmText });
    }

    // Function to update leaderboard data
    const updateLeaderboardUI = (leaderboardData: LeaderboardData[]) => {
        console.log('Updating leaderboard UI with data:', leaderboardData);
        // Ensure we have valid leaderboardEntries
        if (!leaderboardEntries || !leaderboardEntries.length) {
            console.warn('Leaderboard entries not yet initialized');
            return;
        }

        for (let i = 0; i < 10; i++) {
            if (i < leaderboardData.length) {
                const entry = leaderboardData[i];
                // Sanitize username to prevent XSS attacks
                const sanitizedUsername = sanitizeText(entry.username);
                leaderboardEntries[i].name.text = sanitizedUsername;
                leaderboardEntries[i].km.text = entry.kilometers.toFixed(3); // Changed from toFixed(1) to toFixed(3)
            } else {
                leaderboardEntries[i].name.text = "---";
                leaderboardEntries[i].km.text = "0.000"; // Updated to match the new format
            }
        }
    };

    // Store the update function for later use
    (window as any).updateLeaderboardUI = updateLeaderboardUI;

    // Create a grid background for that synthwave look
    // ... existing code ...

    // Add a sun/horizon element for that classic outrun look
    const sun = new Ellipse();
    sun.width = "0.3";
    sun.height = "0.15";
    sun.background = "linear-gradient(180deg, #ff5500 0%, #ff0066 100%)";
    sun.thickness = 0;
    sun.top = "15%"; // Keep moved up to 15%
    container.addControl(sun);

    // Add horizontal lines to create horizon effect
    for (let i = 0; i < 5; i++) {
        const line = new Line();
        line.x1 = 0.35;
        line.y1 = 0.15 + (i * 0.02); // Adjusted position
        line.x2 = 0.65;
        line.y2 = 0.15 + (i * 0.02); // Adjusted position
        line.color = i % 2 === 0 ? "#ff00aa" : "#00ffff"; // Alternate pink and cyan
        line.lineWidth = 3;
        container.addControl(line);
    }

    // Title with gradient text
    const title = new TextBlock();
    title.text = "VIBE DRIVING";
    title.fontFamily = "'Press Start 2P', monospace";
    title.fontSize = window.innerWidth < 600 ? "24px" : "42px";
    title.color = "#ffffff";
    title.outlineWidth = 8;
    title.outlineColor = "#ff00aa";
    title.shadowBlur = 10;
    title.shadowColor = "#00ffff";
    title.top = window.innerWidth < 600 ? "-45%" : "-35%"; // Reset to a reasonable value
    container.addControl(title);

    // Add a pulsing animation to the title
    let time = 0;
    scene.onBeforeRenderObservable.add(() => {
        time += engine.getDeltaTime() / 1000;
        const scale = 1 + 0.02 * Math.sin(time * 2);
        title.fontSize = window.innerWidth < 600 ? `${24 * scale}px` : `${42 * scale}px`;
        title.outlineWidth = 8 + 2 * Math.sin(time * 3);
    });

    // Create a corner banner for multiplayer text
    const cornerBanner = new Rectangle();
    cornerBanner.width = window.innerWidth < 600 ? "220px" : "300px"; // Further increased width to fit text
    cornerBanner.height = window.innerWidth < 600 ? "30px" : "40px";
    cornerBanner.background = "rgba(0, 0, 0, 0.6)";
    cornerBanner.thickness = 0;
    cornerBanner.rotation = Math.PI / 4; // 45 degrees in radians
    cornerBanner.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
    cornerBanner.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    cornerBanner.top = window.innerWidth < 600 ? "50px" : "75px"; // Moved down by ~25 pixels
    cornerBanner.left = window.innerWidth < 600 ? "50px" : "50px";
    container.addControl(cornerBanner);

    // Add the multiplayer text to the banner
    const multiplayerText = new TextBlock();
    multiplayerText.text = "now with multi-player";
    multiplayerText.fontFamily = "'Press Start 2P', monospace";
    multiplayerText.fontSize = window.innerWidth < 600 ? "8px" : "12px";
    multiplayerText.color = "#ffff00"; // Neon yellow
    multiplayerText.underline = true;
    multiplayerText.shadowBlur = 5;
    multiplayerText.shadowColor = "#ffff00";
    multiplayerText.paddingLeft = "10px"; // Add padding to prevent text from being cut off
    multiplayerText.paddingRight = "10px"; // Add padding to prevent text from being cut off
    cornerBanner.addControl(multiplayerText);

    // Subtitle
    const subtitle = new TextBlock();
    subtitle.text = "Feeling sad? Feeling happy? Feeling feelings?\nDrive for as long as you want\nfor as long as you need\nuntil you feel what you want to feel";
    subtitle.fontFamily = "Segoe UI, sans-serif";
    subtitle.fontSize = window.innerWidth < 600 ? "14px" : "20px"; // Smaller on mobile
    subtitle.color = "#00ffff";
    subtitle.lineSpacing = window.innerWidth < 600 ? "5px" : "10px"; // Tighter line spacing on mobile
    subtitle.top = window.innerWidth < 600 ? "-30%" : "-20%"; // Reset to a reasonable value
    container.addControl(subtitle);

    // Controls container with synthwave border
    const controlsPanel = new Rectangle();
    controlsPanel.width = window.innerWidth < 600 ? "90%" : "600px";
    controlsPanel.height = window.innerWidth < 600 ? "160px" : "160px"; // Slightly more compact on mobile
    controlsPanel.cornerRadius = 10;
    controlsPanel.thickness = 4;
    controlsPanel.color = "#ff00aa";
    controlsPanel.background = "rgba(21, 0, 37, 0.8)";
    controlsPanel.top = window.innerWidth < 600 ? "-5%" : "0%"; // Reset to a reasonable value
    container.addControl(controlsPanel);

    // Controls title
    const controlsTitle = new TextBlock();
    controlsTitle.text = "CONTROLS";
    controlsTitle.fontFamily = "'Press Start 2P', monospace";
    controlsTitle.fontSize = window.innerWidth < 600 ? "16px" : "16px"; // Smaller on mobile
    controlsTitle.color = "#ffffff";
    controlsTitle.outlineWidth = 1;
    controlsTitle.outlineColor = "#ff00aa";
    controlsTitle.top = window.innerWidth < 600 ? "-50px" : "-55px";
    controlsPanel.addControl(controlsTitle);

    // Controls text
    const controlsText = new TextBlock();
    controlsText.text = "W - Accelerate\nS - Brake/Reverse\nA - Turn Left\nD - Turn Right";
    controlsText.fontFamily = "Segoe UI, sans-serif";
    controlsText.fontSize = window.innerWidth < 600 ? "14px" : "18px"; // Smaller on mobile
    controlsText.color = "#ffffff";
    controlsText.lineSpacing = window.innerWidth < 600 ? "5px" : "6px"; // Tighter line spacing on mobile
    controlsText.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    controlsPanel.addControl(controlsText);

    // Create a native HTML input element instead of Babylon.js InputText
    const htmlUsernameInput = document.createElement('input');
    htmlUsernameInput.type = 'text';
    htmlUsernameInput.placeholder = 'Enter your username...';
    htmlUsernameInput.style.width = window.innerWidth < 600 ? '75%' : '35%';
    htmlUsernameInput.style.maxWidth = '550px'; // Add maximum width of 550px
    htmlUsernameInput.style.height = '40px';
    htmlUsernameInput.style.color = 'white';
    htmlUsernameInput.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    htmlUsernameInput.style.border = '2px solid #00ffff';
    htmlUsernameInput.style.borderRadius = '5px';
    htmlUsernameInput.style.padding = '0 10px';
    htmlUsernameInput.style.fontFamily = 'Segoe UI, sans-serif';
    htmlUsernameInput.style.fontSize = '18px';
    htmlUsernameInput.style.position = 'absolute';
    htmlUsernameInput.style.left = '50%';
    htmlUsernameInput.style.top = window.innerWidth < 600 ? '63.5%' : '62.5%';
    htmlUsernameInput.style.transform = 'translate(-50%, 0)';
    htmlUsernameInput.style.outline = 'none';
    htmlUsernameInput.style.zIndex = '5'; // Use a very low z-index

    // Create a container for the HTML input to establish a proper stacking context
    const usernameInputContainer = document.createElement('div');
    usernameInputContainer.style.position = 'absolute';
    usernameInputContainer.style.width = '100%';
    usernameInputContainer.style.height = '100%';
    usernameInputContainer.style.top = '0';
    usernameInputContainer.style.left = '0';
    usernameInputContainer.style.zIndex = '5'; // Very low z-index to ensure it's behind the leaderboard
    usernameInputContainer.style.pointerEvents = 'none'; // Allow clicks to pass through the container

    // But allow pointer events on the input itself
    htmlUsernameInput.style.pointerEvents = 'auto';

    // Add the input to the container instead of directly to the body
    usernameInputContainer.appendChild(htmlUsernameInput);
    document.body.appendChild(usernameInputContainer);

    // Add focus styles
    htmlUsernameInput.addEventListener('focus', () => {
        htmlUsernameInput.style.backgroundColor = 'rgba(0, 255, 255, 0.2)';
    });

    htmlUsernameInput.addEventListener('blur', () => {
        htmlUsernameInput.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    });

    // Create a placeholder for the Babylon.js Input for backward compatibility
    // This ensures we don't need to change references elsewhere in the code
    const usernameInput = {
        text: '',
        // Update the text property whenever the HTML input changes
        _htmlInput: htmlUsernameInput
    };

    // Keep the text property updated with the HTML input value
    htmlUsernameInput.addEventListener('input', () => {
        usernameInput.text = htmlUsernameInput.value;
    });

    // Removing the Babylon.js InputText that we're replacing
    // const usernameInput = new InputText();
    // usernameInput.width = "70%";
    // usernameInput.height = "40px"; // Increased height
    // usernameInput.color = "white";
    // usernameInput.background = "rgba(0, 0, 0, 0.5)";
    // usernameInput.placeholderText = "Enter your username...";
    // usernameInput.placeholderColor = "gray";
    // usernameInput.focusedBackground = "rgba(0, 255, 255, 0.2)";
    // usernameInput.thickness = 2;
    // usernameInput.fontFamily = "Segoe UI, sans-serif";
    // usernameInput.fontSize = "18px"; // Increased font size
    // usernamePanel.addControl(usernameInput);

    // Create a DOM button for starting (this is the reliable approach)
    const addMainButton = () => {
        const domBtn = document.createElement('button');
        domBtn.textContent = "START DRIVING";
        domBtn.style.position = 'absolute';
        domBtn.style.top = window.innerWidth < 600 ? '83%' : '85%'; // Position it lower on mobile
        domBtn.style.left = '50%';
        domBtn.style.transform = 'translate(-50%, -50%)';
        domBtn.style.padding = window.innerWidth < 600 ? '8px 15px' : '20px 40px'; // Smaller padding on mobile
        domBtn.style.width = window.innerWidth < 600 ? '50%' : '350px'; // Narrower on mobile
        domBtn.style.maxWidth = '350px';
        domBtn.style.height = window.innerWidth < 600 ? '50px' : '100px'; // Shorter on mobile
        domBtn.style.backgroundColor = '#ff00aa';
        domBtn.style.color = 'white';
        domBtn.style.fontFamily = "'Press Start 2P', monospace";
        domBtn.style.fontSize = window.innerWidth < 600 ? '14px' : '24px'; // Smaller font on mobile
        domBtn.style.border = 'none';
        domBtn.style.borderRadius = '10px';
        domBtn.style.cursor = 'pointer';
        domBtn.style.zIndex = '1000';
        domBtn.style.boxShadow = '0 0 20px #ff00aa';

        // Add hover effects
        domBtn.addEventListener('mouseenter', () => {
            domBtn.style.backgroundColor = '#00ffff';
            domBtn.style.color = '#000000';
        });

        domBtn.addEventListener('mouseleave', () => {
            domBtn.style.backgroundColor = '#ff00aa';
            domBtn.style.color = '#ffffff';
        });

        // The click handler - this function starts the driving mode
        domBtn.onclick = () => {
            console.log("DOM START DRIVING button clicked!");
            startDriving();
        };

        document.body.appendChild(domBtn);

        // Add copyright text below the button as a DOM element
        const copyright = document.createElement('div');

        const copyrightText = document.createTextNode("© 2025 VIBE DRIVING SIMULATOR made by ");

        // Create the username link with proper styling
        const usernameLink = document.createElement('a');
        usernameLink.textContent = "@philipreichen";
        usernameLink.href = "https://x.com/philipreichen";
        usernameLink.target = "_blank"; // Open in new tab
        usernameLink.style.color = 'inherit';
        usernameLink.style.textDecoration = 'underline';
        usernameLink.style.cursor = 'pointer';

        // Add the elements to the copyright div
        copyright.appendChild(copyrightText);
        copyright.appendChild(usernameLink);

        copyright.style.position = 'absolute';
        // Adjust top position based on width
        copyright.style.top = window.innerWidth <= 382 ? '97%' : (window.innerWidth <= 543 ? '98%' : (window.innerWidth < 600 ? '99%' : '98%'));

        if (window.innerWidth < 600) {
            // Left-aligned for small screens with multi-line layout
            copyright.style.left = '20px';
            copyright.style.transform = 'translateY(-50%)';
            copyright.style.textAlign = 'left';
            copyright.style.width = 'calc(100% - 180px)'; // Full width minus margins
            copyright.style.whiteSpace = 'normal'; // Allow text to wrap
        } else {
            // Center-aligned for larger screens
            copyright.style.left = '50%';
            copyright.style.transform = 'translate(-50%, -50%)';
            copyright.style.textAlign = 'center';
            copyright.style.width = 'auto';
            copyright.style.whiteSpace = 'nowrap'; // Prevent text wrapping
        }

        copyright.style.color = '#ff00aa';
        copyright.style.fontFamily = "'Press Start 2P', monospace";
        copyright.style.fontSize = window.innerWidth < 600 ? '7px' : '12px'; // Even smaller on mobile
        copyright.style.zIndex = '1000';
        copyright.style.textShadow = '0 0 5px #ff00aa';
        document.body.appendChild(copyright);

        return {
            button: domBtn,
            copyright: copyright
        };
    };

    const domElements = addMainButton();
    const startButton = domElements.button;
    const copyrightDom = domElements.copyright;
    // No need to store in domElements, we already have a direct reference as leaderboardButton

    // Create a simplified driving start function
    const startDriving = () => {
        // Prevent multiple activations
        if (drivingEnabled) return;

        console.log('🚗 START DRIVING function called!');

        try {
            // Remove only the DOM button, but keep the copyright
            if (startButton) {
                startButton.remove();
            }
            // No longer removing the copyright element - keep it visible during gameplay
            // Position it at the bottom during gameplay
            if (copyrightDom) {
                // Adjust top position based on width for gameplay
                copyrightDom.style.top = window.innerWidth <= 382 ? '97%' : (window.innerWidth <= 543 ? '98%' : (window.innerWidth < 600 ? '99%' : '98%'));
                // Ensure it stays on top of other elements
                copyrightDom.style.zIndex = '2000';
            }

            // Hide the leaderboard and statistics buttons when the game starts
            if (leaderboardButton) {
                leaderboardButton.style.display = 'none';
                console.log("Hidden leaderboard button");
            }

            if (statisticsButton) {
                statisticsButton.style.display = 'none';
                console.log("Hidden statistics button");
            }

            // Add a flash effect
            const flash = new Rectangle();
            flash.width = 1;
            flash.height = 1;
            flash.background = "#ffffff";
            flash.thickness = 0;
            flash.alpha = 0.8;
            uiTexture.addControl(flash);

            // Flash animation
            let flashAlpha = 0.8;
            const flashInterval = setInterval(() => {
                flashAlpha -= 0.1;
                flash.alpha = flashAlpha;

                if (flashAlpha <= 0) {
                    clearInterval(flashInterval);
                    console.log("Flash animation complete, disposing UI...");

                    try {
                        // Get the username from the HTML input field
                        const rawUsername = htmlUsernameInput.value;

                        if (rawUsername && rawUsername.trim().length > 0) {
                            // If a username is provided, use it without validation
                            username = rawUsername.trim();
                            console.log("Using provided username:", username);
                        } else {
                            // Generate a random username if none is provided
                            username = "Driver_" + Math.floor(Math.random() * 1000);
                            console.log("No username provided, using generated username:", username);
                        }

                        // Remove UI elements
                        container.dispose();
                        flash.dispose();

                        // Remove the HTML input container and leaderboard button
                        usernameInputContainer.remove(); // Remove the container which includes the input

                        // Remove the HTML leaderboard button
                        if (leaderboardButton) {
                            leaderboardButton.remove();
                            console.log("Removed HTML leaderboard button");
                        } else {
                            console.log("Could not find leaderboard button to remove");
                        }

                        // Remove the HTML statistics button
                        if (statisticsButton) {
                            statisticsButton.remove();
                            console.log("Removed HTML statistics button");
                        }

                        // Remove the HTML close buttons
                        if (statisticsCloseBtn) {
                            statisticsCloseBtn.remove();
                            console.log("Removed HTML statistics close button");
                        }

                        if (leaderboardCloseBtn) {
                            leaderboardCloseBtn.remove();
                            console.log("Removed HTML leaderboard close button");
                        }

                        // Enable driving controls
                        console.log("Enabling driving controls...");
                        drivingEnabled = true;
                        sceneWithControls.enableDriving();

                        // Show mobile controls
                        if (typeof window.showMobileControls === 'function') {
                            window.showMobileControls(true);
                        }

                        // Log confirmation
                        console.log("🚗 Driving is now enabled! Use WASD to drive.");

                        // Add a visual notification that driving is enabled
                        const notification = new TextBlock();
                        notification.text = "LET'S GO, BABY!!!";
                        notification.color = "#ffffff";
                        notification.fontSize = "24px";
                        notification.fontFamily = "'Press Start 2P', monospace";
                        notification.outlineWidth = 2;
                        notification.outlineColor = "#ff00aa";
                        notification.top = "-40%";

                        const notificationUI = AdvancedDynamicTexture.CreateFullscreenUI("notificationUI");
                        notificationUI.addControl(notification);

                        // Make the notification disappear after 3 seconds
                        setTimeout(() => {
                            notification.dispose();
                        }, 3000);

                        // Make speedometer and kilometer counter visible
                        const speedDisplay = document.querySelector('div[style*="bottom: 20px"][style*="left: 20px"]') as HTMLElement;
                        const kmCounter = document.querySelector('div[style*="top: 20px"][style*="right: 20px"]') as HTMLElement;

                        if (speedDisplay) speedDisplay.style.display = 'block';
                        if (kmCounter) kmCounter.style.display = 'block';

                        // Make sure engine sound is always initialized and unmuted
                        if (window.initializeEngineSound) {
                            window.initializeEngineSound();
                        }

                        // Load kilometers from localStorage when game starts
                        sceneWithControls.loadKilometersFromStorage();
                    } catch (error) {
                        console.error("Error during UI cleanup:", error);
                    }
                }
            }, 50);
        } catch (error) {
            console.error("Error in start button click handler:", error);
        }
    };

    // Attach the startDriving function to the window for debugging
    (window as any).startDriving = startDriving;

    // Add keyboard event listeners to start driving
    window.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !drivingEnabled) {
            console.log('Starting driving via keyboard shortcut:', e.key);
            (window as any).startDriving();
        }
    });

    // Credits at the bottom
    const credits = new TextBlock();
    credits.text = "© 2025 VIBE DRIVING SIMULATOR";
    credits.fontFamily = "'Press Start 2P', monospace";
    credits.fontSize = window.innerWidth < 600 ? "8px" : "12px";
    credits.color = "#ff00aa";
    credits.top = window.innerWidth < 600 ? "65%" : "90%"; // Move much higher on mobile
    container.addControl(credits);

    // Add a responsive handler for window resizing
    window.addEventListener('resize', () => {
        // Update title size and position
        title.fontSize = window.innerWidth < 600 ? "24px" : "42px";
        title.top = window.innerWidth < 600 ? "-45%" : "-35%";

        // Update subtitle size and position
        subtitle.fontSize = window.innerWidth < 600 ? "14px" : "20px";
        subtitle.lineSpacing = window.innerWidth < 600 ? "5px" : "10px";
        subtitle.top = window.innerWidth < 600 ? "-25%" : "-20%";

        // Update controls panel size and position
        controlsPanel.width = window.innerWidth < 600 ? "90%" : "600px";
        controlsPanel.height = window.innerWidth < 600 ? "160px" : "160px";
        controlsPanel.top = window.innerWidth < 600 ? "-5%" : "0%";

        // Update controls text
        controlsTitle.fontSize = window.innerWidth < 600 ? "16px" : "22px";
        controlsText.fontSize = window.innerWidth < 600 ? "14px" : "18px";
        controlsText.lineSpacing = window.innerWidth < 600 ? "5px" : "10px";

        // Update credits
        credits.fontSize = window.innerWidth < 600 ? "8px" : "12px";
        credits.top = window.innerWidth < 600 ? "65%" : "90%";

        // Update HTML username input position
        htmlUsernameInput.style.top = window.innerWidth < 600 ? '63.5%' : '62.5%';
        htmlUsernameInput.style.width = window.innerWidth < 600 ? '75%' : '35%';
        htmlUsernameInput.style.maxWidth = '550px'; // Ensure max width is maintained on resize

        // Update copyright text position
        if (copyrightDom) {
            copyrightDom.style.top = window.innerWidth <= 382 ? '97%' : (window.innerWidth <= 543 ? '98%' : (window.innerWidth < 600 ? '99%' : '98%'));
            copyrightDom.style.fontSize = window.innerWidth < 600 ? '7px' : '12px';
            if (window.innerWidth < 600) {
                copyrightDom.style.left = '20px';
                copyrightDom.style.transform = 'translateY(-50%)';
                copyrightDom.style.textAlign = 'left';
                copyrightDom.style.width = 'calc(100% - 180px)';
                copyrightDom.style.whiteSpace = 'normal';
            } else {
                copyrightDom.style.left = '50%';
                copyrightDom.style.transform = 'translate(-50%, -50%)';
                copyrightDom.style.textAlign = 'center';
                copyrightDom.style.width = 'auto';
                copyrightDom.style.whiteSpace = 'nowrap';
            }
        }

        // Other styles don't need to be updated as they use percentages and will scale correctly

        // Reposition the modal close buttons if visible
        if (statisticsPanel && statisticsPanel.isVisible) {
            positionStatisticsCloseBtn();
        }
        if (leaderboardPanel && leaderboardPanel.isVisible) {
            positionLeaderboardCloseBtn();
        }
    });

    // Create a helper function to set up all socket event listeners
    const setupSocketEventListeners = (socket: Socket) => {
        console.log('Setting up all socket event listeners');

        // Statistics event listener
        socket.on('statisticsData', (data) => {
            console.log('Received statistics data:', data);

            // Map server property names to frontend property names
            const mappedData = {
                totalPlayers: data.totalPlayersConnected,
                totalMinutesPlayed: data.totalMinutesPlayed,
                totalKilometersDriven: data.totalKilometersDriven
            };

            // Store the mapped data in the statisticsData variable
            statisticsData = mappedData;

            if ((window as any).updateStatisticsUI) {
                (window as any).updateStatisticsUI(mappedData);
            }
        });

        // Leaderboard event listener
        socket.on('leaderboardData', (data) => {
            console.log('Received leaderboard data:', data);
            if ((window as any).updateLeaderboardUI) {
                (window as any).updateLeaderboardUI(data);
            }
        });

        // Connection error handler
        socket.on('connect_error', (error) => {
            console.error('Failed to connect to server:', error);

            // Update leaderboard UI if visible
            if (leaderboardPanel.isVisible && leaderboardEntries && leaderboardEntries.length > 0) {
                leaderboardEntries[0].name.text = "CONNECTION ERROR";
                leaderboardEntries[0].km.text = "!";
                for (let i = 1; i < leaderboardEntries.length; i++) {
                    leaderboardEntries[i].name.text = "---";
                    leaderboardEntries[i].km.text = "---";
                }
            }

            // Update statistics UI if visible
            if (statisticsPanel.isVisible) {
                if (window.innerWidth < 600) {
                    // For small screens
                    totalPlayersLabelText.text = "CONNECTION";
                    totalPlayersValueText.text = "ERROR";

                    totalMinutesLabelText.text = "Please try";
                    totalMinutesValueText.text = "again later";

                    totalKilometersLabelText.text = "";
                    totalKilometersValueText.text = "";
                } else {
                    // For larger screens
                    totalPlayersLabelText.text = "CONNECTION ERROR";
                    totalMinutesLabelText.text = "Please try again later";
                    totalKilometersLabelText.text = "";
                }
            }
        });

        return socket;
    };

    // Add window resize handler to reposition buttons when window size changes
    window.addEventListener('resize', () => {
        // Only reposition if visible
        if (statisticsPanel.isVisible) {
            positionStatisticsCloseBtn();
        }
        if (leaderboardPanel.isVisible) {
            positionLeaderboardCloseBtn();
        }
    });

    return uiTexture;
};

// Create start screen
const startScreen = createStartScreen();

// Add keyboard shortcut for starting (Enter only)
window.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !drivingEnabled) {
        console.log('Starting driving via keyboard shortcut:', e.key);
        (window as any).startDriving();
    }
});

// Add clear console debugging instructions
console.log('%c VIBE DRIVING DEBUG GUIDE ', 'background: #ff00aa; color: white; font-size: 14px; font-weight: bold; padding: 4px;');
console.log('%c • To start driving, click the "START DRIVING" button', 'color: #00ffff');
console.log('%c • Alternative: Press ENTER key to start driving', 'color: #00ffff');
console.log('%c • After starting, use WASD keys to drive', 'color: #00ffff');
console.log('%c • Check browser console for debug messages', 'color: #00ffff');
console.log('%c • If button doesn\'t work, try clicking the button area directly', 'color: #00ffff');

// Register a render loop to update the scene
engine.runRenderLoop(() => {
    scene.render();
});

// Handle window resize
window.addEventListener('resize', () => {
    engine.resize();
});

// Additional resize handler for UI elements during gameplay
window.addEventListener('resize', () => {
    if (drivingEnabled) {
        const copyrightDom = document.querySelector('div[style*="font-family: \'Press Start 2P\'"]') as HTMLElement;
        if (copyrightDom) {
            copyrightDom.style.top = window.innerWidth <= 382 ? '97%' : (window.innerWidth <= 543 ? '98%' : (window.innerWidth < 600 ? '99%' : '98%'));
            copyrightDom.style.fontSize = window.innerWidth < 600 ? '7px' : '12px';
            if (window.innerWidth < 600) {
                copyrightDom.style.left = '20px';
                copyrightDom.style.transform = 'translateY(-50%)';
                copyrightDom.style.textAlign = 'left';
                copyrightDom.style.width = 'calc(100% - 180px)';
                copyrightDom.style.whiteSpace = 'normal';
            } else {
                copyrightDom.style.left = '50%';
                copyrightDom.style.transform = 'translate(-50%, -50%)';
                copyrightDom.style.textAlign = 'center';
                copyrightDom.style.width = 'auto';
                copyrightDom.style.whiteSpace = 'nowrap';
            }
        }
    }
});

// Create the assets directory if it doesn't exist
const setupAssetsDirectory = () => {
    console.log("Looking for custom images in:", customImagePaths);

    // Check if assets exist during development/testing
    customImagePaths.forEach(path => {
        fetch(path)
            .then(response => {
                if (response.ok) {
                    console.log(`✅ File exists and is accessible: ${path}`);
                } else {
                    console.warn(`❌ File not found or inaccessible: ${path} (status: ${response.status})`);
                }
            })
            .catch(error => {
                console.error(`❌ Error checking file: ${path}`, error);
            });
    });
};

// Add this function to create the billboard texture content
const createCustomBillboardTexture = (billboard: any, segmentIndex: number) => {
    // Create a dynamic texture for the billboard text
    const textureWidth = 512;
    const textureHeight = 256;
    const dynamicTexture = new DynamicTexture(`billboardTexture_${segmentIndex}`,
        { width: textureWidth, height: textureHeight }, scene, false);

    // Get the context for drawing
    const textureContext = dynamicTexture.getContext();

    // Fill the background
    textureContext.fillStyle = "#000000"; // Black background instead of dark gray
    textureContext.fillRect(0, 0, textureWidth, textureHeight);

    // Draw the text - need to cast to CanvasRenderingContext2D to access text properties
    const ctx = textureContext as unknown as CanvasRenderingContext2D;
    ctx.font = "bold 64px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#FFFFFF"; // Pure white text
    ctx.fillText("Click to buy Ad", textureWidth / 2, textureHeight / 2 - 30);

    // Add a second line of text in a different color
    ctx.font = "bold 40px Arial";
    ctx.fillStyle = "#FFFFFF"; // Also white text instead of cyan
    ctx.fillText("$10,000/month", textureWidth / 2, textureHeight / 2 + 40);

    // Update the texture
    dynamicTexture.update();

    // Create material with the texture
    const billboardMaterial = new StandardMaterial(`billboardMat_${segmentIndex}`, scene);
    billboardMaterial.diffuseTexture = dynamicTexture;
    billboardMaterial.specularColor = new Color3(0.2, 0.2, 0.2); // Subtle shine
    billboardMaterial.backFaceCulling = false; // Show both sides
    billboardMaterial.emissiveColor = new Color3(1, 1, 1); // Make the texture glow at full brightness
    billboardMaterial.ambientColor = new Color3(1, 1, 1); // Full ambient lighting

    // Apply material to the billboard
    billboard.material = billboardMaterial;

    // Make billboard clickable - open Stripe URL in new tab
    billboard.actionManager = new ActionManager(scene);
    billboard.actionManager.registerAction(
        new ExecuteCodeAction(
            ActionManager.OnPickTrigger,
            () => {
                window.open('https://buy.stripe.com/bIYeVI8bf7fc1TacMM', '_blank');
            }
        )
    );

    // Add hover cursor effect - change cursor to pointer when hovering over billboard
    billboard.actionManager.registerAction(
        new ExecuteCodeAction(
            ActionManager.OnPointerOverTrigger,
            () => {
                document.body.style.cursor = 'pointer';
            }
        )
    );

    // Reset cursor when no longer hovering
    billboard.actionManager.registerAction(
        new ExecuteCodeAction(
            ActionManager.OnPointerOutTrigger,
            () => {
                document.body.style.cursor = 'default';
            }
        )
    );

    // Add a neon border around the billboard
    // Create frame as individual edges to better control appearance
    const frameThickness = 0.15;
    const frameDepth = 0.05;

    // Create neon frame material
    const frameMaterial = new StandardMaterial(`frameMatBillboard_${segmentIndex}`, scene);
    frameMaterial.diffuseColor = new Color3(0, 1, 1); // Cyan
    frameMaterial.emissiveColor = new Color3(0, 1, 1); // Make it glow
    frameMaterial.specularColor = new Color3(1, 1, 1); // Shiny

    // Top edge
    const topFrame = MeshBuilder.CreateBox(
        `topFrame_${segmentIndex}`,
        { width: 9 + frameThickness, height: frameThickness, depth: frameDepth },
        scene
    );
    topFrame.parent = billboard;
    topFrame.position.y = 2.25 + frameThickness / 2;
    topFrame.position.z = -frameDepth / 2;
    topFrame.material = frameMaterial;

    // Bottom edge
    const bottomFrame = MeshBuilder.CreateBox(
        `bottomFrame_${segmentIndex}`,
        { width: 9 + frameThickness, height: frameThickness, depth: frameDepth },
        scene
    );
    bottomFrame.parent = billboard;
    bottomFrame.position.y = -2.25 - frameThickness / 2;
    bottomFrame.position.z = -frameDepth / 2;
    bottomFrame.material = frameMaterial;

    // Left edge
    const leftFrame = MeshBuilder.CreateBox(
        `leftFrame_${segmentIndex}`,
        { width: frameThickness, height: 4.5 + frameThickness, depth: frameDepth },
        scene
    );
    leftFrame.parent = billboard;
    leftFrame.position.x = -4.5 - frameThickness / 2;
    leftFrame.position.z = -frameDepth / 2;
    leftFrame.material = frameMaterial;

    // Right edge
    const rightFrame = MeshBuilder.CreateBox(
        `rightFrame_${segmentIndex}`,
        { width: frameThickness, height: 4.5 + frameThickness, depth: frameDepth },
        scene
    );
    rightFrame.parent = billboard;
    rightFrame.position.x = 4.5 + frameThickness / 2;
    rightFrame.position.z = -frameDepth / 2;
    rightFrame.material = frameMaterial;
};

